import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { supabase } from "../supabaseClient";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserPlus, faCalendar, faShareFromSquare, faPaperPlane, faPlus, faTasks, faPen, faTrashCan, faFolder } from "@fortawesome/free-solid-svg-icons";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import "./AtaObjetivos.css";
import AtaObjetivosSkeleton from "./skeletons/AtaObjetivosSkeleton";

const PREFIXO_EXCLUIDO = "[EXCLUIDO]";

// ── Persistência de filtros entre atas/notas ──
// Sobrevive a trocas de ata/nota (variável de módulo); reseta só ao recarregar a página.
const filtrosPersistidos = {
  ordenacao: 'recentes',
  arquivarConcluidos: false,
  soSemResp: false,
  soEmAberto: false,
  filtroResponsavel: null,
  filtroEntidade: null,
};

// Helper para renderizar negrito/itálico
const INLINE_FORMAT_REGEX = /\*\*([^\n]+?)\*\*|\*([^\n*]+?)\*/g;

function renderTextoFormatado(raw) {
  if (!raw) return raw;
  const parts = [];
  let lastIndex = 0;
  let key = 0;
  let m;
  INLINE_FORMAT_REGEX.lastIndex = 0;
  while ((m = INLINE_FORMAT_REGEX.exec(raw)) !== null) {
    if (m.index > lastIndex) parts.push(raw.slice(lastIndex, m.index));
    parts.push(m[1] !== undefined ? <strong key={key++}>{m[1]}</strong> : <em key={key++}>{m[2]}</em>);
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < raw.length) parts.push(raw.slice(lastIndex));
  return parts;
}

// ── HELPERS PARA CADEIA DE ORIGEM ──
const parseOrigemChain = (comentario) => {
  const matchChain = comentario?.match(/\[ORIGEM_CHAIN:([\s\S]+)\]$/);
  if (matchChain) {
    try {
      const parsed = JSON.parse(matchChain[1]);
      if (Array.isArray(parsed)) return parsed;
    } catch { /* cai no fallback abaixo */ }
  }
  const matchLegado = comentario?.match(/\[ORIGEM_TEXTO:([\s\S]+?)\]/);
  if (matchLegado) return [{ id: null, texto: matchLegado[1] }];
  return [];
};

const buildOrigemChainTag = (chain) => `[ORIGEM_CHAIN:${JSON.stringify(chain)}]`;

// ── HELPERS: tópicos /* ... */ não podem entrar em objetivos ──
const TOPICO_REGEX = /\/\*[^\n]+?\*\//g;
const SEPARADOR_TOPICO = "\u0000";

const removerTopicos = (texto) => (texto || "").replace(TOPICO_REGEX, SEPARADOR_TOPICO);

const textoUsaAspasSimples = (texto) => /'([^'\u0000]+)'/.test(removerTopicos(texto));

const extrairObjetivosValidos = (texto) => {
  if (!texto?.trim()) return [];
  const limpo = removerTopicos(texto);
  const regex = /'([^'\u0000]+)'/g;
  const matches = [];
  let match;
  while ((match = regex.exec(limpo)) !== null) {
    const objetivo = match[1].trim();
    if (objetivo) matches.push(objetivo);
  }
  return matches;
};

const podeDesmarcarConclusao = (concluidoEm) => {
  if (!concluidoEm) return true;
  return (new Date() - new Date(concluidoEm)) / (1000 * 60 * 60) < 24;
};

const gerarAbreviacao = (nome) => {
  if (!nome) return "?";
  if (nome.includes('_')) return nome.split('_').map(p => p.charAt(0).toUpperCase()).join('_');
  if (nome.includes(' ')) return nome.split(' ').filter(p => p.length > 0).map(p => p.charAt(0).toUpperCase()).join('');
  return nome.substring(0, 2).charAt(0).toUpperCase() + nome.substring(1, 2).toLowerCase();
};

const buscarEmLotes = async (tabela, coluna, ids, select = "*") => {
  const saida = [];
  for (let c = 0; c < ids.length; c += 100) {
    const { data, error } = await supabase.from(tabela).select(select).in(coluna, ids.slice(c, c + 100));
    if (error) { console.error("[carga]", tabela, error); continue; }
    saida.push(...(data || []));
  }
  return saida;
};

const buscarObjetivosPaginado = async (ataId) => {
  const acumulado = [];
  let erro = null;
  for (let from = 0; ; from += 1000) {
    const { data, error } = await supabase
      .from("ata_objetivos")
      .select("*")
      .eq("ata_id", ataId)
      .order("ordem", { ascending: true })
      .order("id", { ascending: true })
      .range(from, from + 999);
    if (error) { erro = error; break; }
    acumulado.push(...(data || []));
    if (!data || data.length < 1000) break;
  }
  return { data: acumulado, error: erro };
};

const buscarTextosExistentesPaginado = async (ataId) => {
  const existentes = new Set();
  for (let from = 0; ; from += 1000) {
    const { data, error } = await supabase.from("ata_objetivos").select("texto").eq("ata_id", ataId).range(from, from + 999);
    if (error || !data) break;
    data.forEach(r => {
      const limpo = (r.texto || "").replace(PREFIXO_EXCLUIDO, "").trim().toLowerCase();
      if (limpo) existentes.add(limpo);
    });
    if (data.length < 1000) break;
  }
  return existentes;
};

const buscarPilhasDaEntidadeComCompartilhadas = async (entidade) => {
  const campoPilha = entidade.tipo === 'projeto' ? 'project_id' : 'setor_id';
  const tipoComp = entidade.tipo === 'projeto' ? 'project' : 'setor';

  const { data: proprias, error: erroProprias } = await supabase.from("pilhas").select("id, title, tipo_pilha").eq(campoPilha, entidade.id);
  if (erroProprias) throw erroProprias;

  const { data: vinculos, error: erroVinc } = await supabase
    .from("pilhas_compartilhamentos").select("pilha_id").eq("entidade_tipo", tipoComp).eq("entidade_id", entidade.id);
  if (erroVinc) throw erroVinc;

  const idsProprios = new Set((proprias || []).map(p => p.id));
  const idsCompartilhados = [...new Set((vinculos || []).map(v => v.pilha_id))].filter(id => !idsProprios.has(id));

  let compartilhadas = [];
  if (idsCompartilhados.length > 0) {
    const { data, error } = await supabase.from("pilhas").select("id, title, tipo_pilha").in("id", idsCompartilhados);
    if (error) throw error;
    compartilhadas = data || [];
  }

  return [...(proprias || []), ...compartilhadas].filter(p => p.tipo_pilha !== "sistema_calendario");
};

const ComentarioIcon = ({ onClick, title, hasComments, isLoading, count }) => {
  const cor = hasComments ? '#10b981' : '#9ca3af';
  return (
    <span
      className="obj-icon-btn obj-icon-btn--comentario"
      style={{ color: cor, cursor: isLoading ? 'default' : 'pointer' }}
      onClick={isLoading ? undefined : onClick}
      title={isLoading ? 'Carregando...' : title}
    >
      {isLoading ? (
        <span className="spinner-comentario" />
      ) : (
        <>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
          {count > 0 && <span className="obj-comentario-badge">{count > 99 ? '99+' : count}</span>}
        </>
      )}
    </span>
  );
};

const LinkNotaIcon = ({ onClick, title, temLink, isDisabled }) => {
  const cor = temLink ? '#10b981' : '#9ca3af';
  return (
    <span
      className="obj-icon-btn"
      style={{ color: cor, cursor: isDisabled ? 'not-allowed' : 'pointer', opacity: isDisabled ? 0.5 : 1 }}
      onClick={isDisabled ? undefined : onClick}
      title={title}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
      </svg>
    </span>
  );
};

const ChipResponsavel = ({ responsavel, onRemove, disabled, colaboradoresAtivosIds }) => {
  const nomeExibicao = responsavel.nome_exibicao || "Usuário";
  const isExterno    = !responsavel.usuario_id;
  const removido     = !isExterno && colaboradoresAtivosIds && !colaboradoresAtivosIds.has(responsavel.usuario_id);
  const avatarUrl    = responsavel.avatar_url;
  const abreviacao   = gerarAbreviacao(nomeExibicao);
  const funcao       = responsavel.funcao || (isExterno ? "Externo" : "");

  return (
    <span
      className={`chip-resp-mini ${isExterno ? 'chip-resp-mini--ext' : ''} ${removido ? 'chip-resp-mini--removido' : ''}`}
      title={removido ? `Usuário removido do container — ${nomeExibicao}` : (funcao ? `${nomeExibicao} · ${funcao}` : nomeExibicao)}
    >
      {removido ? (
        <div className="chip-resp-mini-sombra">?</div>
      ) : avatarUrl && !isExterno ? (
        <img src={avatarUrl} alt={nomeExibicao} className="chip-resp-mini-img"
          onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
      ) : null}
      {!removido && (
        <div className="chip-resp-mini-iniciais" style={{ display: avatarUrl && !isExterno ? 'none' : 'flex' }}>
          {abreviacao}
        </div>
      )}
      {!disabled && (
        <span className="chip-resp-mini-remove" onClick={e => { e.stopPropagation(); onRemove(responsavel); }}>×</span>
      )}
      <div className="chip-resp-tooltip">
        <strong>{nomeExibicao}</strong>
        {removido ? <em>Removido do container</em> : (funcao && <span>{funcao}</span>)}
        {isExterno && !removido && <em>Externo</em>}
      </div>
    </span>
  );
};

const ChipEntidade = ({ entidade, onRemove, disabled }) => {
  const isProjeto  = entidade.entidadeTipo === 'projeto';
  const abreviacao = gerarAbreviacao(entidade.entidadeNome);

  return (
    <span
      className={`chip-entidade-mini ${isProjeto ? 'chip-entidade-mini--projeto' : 'chip-entidade-mini--setor'}`}
      title={`${isProjeto ? 'Projeto' : 'Setor'}: ${entidade.entidadeNome}`}
    >
      {entidade.entidadeFoto && isProjeto ? (
        <img src={entidade.entidadeFoto} alt={entidade.entidadeNome} className="chip-entidade-mini-img"
          onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
      ) : null}
      <div className="chip-entidade-mini-iniciais" style={{ display: entidade.entidadeFoto && isProjeto ? 'none' : 'flex' }}>
        {abreviacao}
      </div>
      {!disabled && (
        <span className="chip-entidade-mini-remove" onClick={e => { e.stopPropagation(); onRemove(entidade); }}>×</span>
      )}
    </span>
  );
};

const MenuPosConclusao = ({ objetivo, objetivoIndex, onNovoObjetivo, onCriarTarefa, onClose }) => (
  <div className="menu-pos-conclusao">
    <div className="menu-pos-conclusao-titulo">O que fazer a seguir?</div>
    <button className="menu-pos-conclusao-btn" onClick={() => { onNovoObjetivo(objetivo, objetivoIndex); onClose(); }}>
      <FontAwesomeIcon icon={faPlus} />
      <span>Novo objetivo</span>
    </button>
    <button className="menu-pos-conclusao-btn menu-pos-conclusao-btn--tarefa"
      onClick={() => { onCriarTarefa(objetivo, objetivoIndex); onClose(); }}>
      <FontAwesomeIcon icon={faTasks} />
      <span>Criar tarefa</span>
    </button>
    <button className="menu-pos-conclusao-fechar" onClick={onClose} title="Fechar">×</button>
  </div>
);

export default function AtaObjetivos({
  ataId,
  texto,
  usuarioId,
  projetoAtual,
  notaAtual,
  projetoNome,
  autorNome,
  onProgressoChange,
  containerAtual,
  onOpenTask,
  criarObjetivosExterno,
  onCriarObjetivosChange,
  buscaObjetivos = ""
}) {
  const [loading, setLoading] = useState(true);
  const criarObjetivos = criarObjetivosExterno ?? false;
  const setCriarObjetivos = onCriarObjetivosChange ?? (() => {});
  const [objetivosList, setObjetivosList]               = useState([]);
  const [sugestoesResponsavel, setSugestoesResponsavel] = useState({});
  const [editandoResp, setEditandoResp]                 = useState({});
  const [editandoComentario, setEditandoComentario]     = useState({});
  const [comentarioTemp, setComentarioTemp]             = useState({});
  const [comentariosObjetivo, setComentariosObjetivo]   = useState({});
  const [enviandoComentario, setEnviandoComentario]     = useState({});
  const [meuNome, setMeuNome]                           = useState("Você");
  const [inputResponsavel, setInputResponsavel]         = useState({});
  const [mostrarAtasDisponiveis, setMostrarAtasDisponiveis] = useState({});
  const [atasDisponiveis, setAtasDisponiveis]           = useState([]);
  const [atasOrigemNomes, setAtasOrigemNomes]           = useState({});

  // ── Filtros persistidos (variável de módulo) ──
  const [filtroResponsavel, setFiltroResponsavel]       = useState(filtrosPersistidos.filtroResponsavel);
  const [filtroEntidade, setFiltroEntidade]             = useState(filtrosPersistidos.filtroEntidade);
  const [arquivarConcluidos, setArquivarConcluidos]     = useState(filtrosPersistidos.arquivarConcluidos);
  const [soSemResp, setSoSemResp]                       = useState(filtrosPersistidos.soSemResp);
  const [soEmAberto, setSoEmAberto]                     = useState(filtrosPersistidos.soEmAberto);
  const [ordenacao, setOrdenacao]                       = useState(filtrosPersistidos.ordenacao);
  const ordemDesc = ordenacao === 'recentes';

  useEffect(() => {
    filtrosPersistidos.ordenacao = ordenacao;
    filtrosPersistidos.arquivarConcluidos = arquivarConcluidos;
    filtrosPersistidos.soSemResp = soSemResp;
    filtrosPersistidos.soEmAberto = soEmAberto;
    filtrosPersistidos.filtroResponsavel = filtroResponsavel;
    filtrosPersistidos.filtroEntidade = filtroEntidade;
  }, [ordenacao, arquivarConcluidos, soSemResp, soEmAberto, filtroResponsavel, filtroEntidade]);

  const [menuPosConclusao, setMenuPosConclusao]         = useState(null);
  const [anexosPorObjetivo, setAnexosPorObjetivo]       = useState({});
  const [uploadandoAnexo, setUploadandoAnexo]           = useState({});
  const [reprogramacoesPorObjetivo, setReprogramacoesPorObjetivo] = useState({});
  const [historicoAberto, setHistoricoAberto]           = useState({});
  const [editandoTexto, setEditandoTexto]               = useState({});
  const [textoTemp, setTextoTemp]                       = useState({});

  const [concluidosRecentes, setConcluidosRecentes] = useState({});
  const timeoutsConclusaoRef = useRef({});

  const [comentariosCount, setComentariosCount] = useState({});
  const [carregandoComentarios, setCarregandoComentarios] = useState({});

  const [respondidosPorMim, setRespondidosPorMim] = useState(new Set());

  const [seletorEntidadeAberto, setSeletorEntidadeAberto] = useState({});
  const [buscaEntidadeObjetivo, setBuscaEntidadeObjetivo] = useState({});
  const [entidadesContainer, setEntidadesContainer]       = useState(null);
  const [carregandoEntidadesContainer, setCarregandoEntidadesContainer] = useState(false);
  const seletorEntidadeRef = useRef({});

  const [colaboradoresAtivosIds, setColaboradoresAtivosIds] = useState(null);

  const [linkandoNotaIndex, setLinkandoNotaIndex]       = useState(null);
  const [notaLinkView, setNotaLinkView]                 = useState('entidades');
  const [entidadesDisponiveis, setEntidadesDisponiveis] = useState([]);
  const [entidadeSelecionada, setEntidadeSelecionada]   = useState(null);
  const [notasDisponiveis, setNotasDisponiveis]         = useState([]);
  const [carregandoNotas, setCarregandoNotas]           = useState(false);
  const [buscaLinkNota, setBuscaLinkNota]               = useState("");

  const [criandoTarefaIndex, setCriandoTarefaIndex]     = useState(null);
  const [tarefaView, setTarefaView]                     = useState('entidades');
  const [entidadesDisponivelsTarefa, setEntidadesDisponivelsTarefa] = useState([]);
  const [entidadeSelecionadaTarefa, setEntidadeSelecionadaTarefa] = useState(null);
  const [pilhasDisponivelsTarefa, setPilhasDisponivelsTarefa] = useState([]);
  const [carregandoPilhasTarefa, setCarregandoPilhasTarefa] = useState(false);
  const [buscaTarefa, setBuscaTarefa]                   = useState("");

  const fileInputRefs = useRef({});
  const dateInputRefs = useRef({});

  const objetivoRefs = useRef({});

  const isSaving      = useRef(false);
  const lastTextRef   = useRef("");
  const isUpdatingRef = useRef(false);
  const excluidosRef  = useRef(new Set());
  const carregandoRef = useRef(false);

  const [loadVersion, setLoadVersion] = useState(0);
  const loadIdRef = useRef(0);

  const scrollParaObjetivo = useCallback((id) => {
    const el = objetivoRefs.current[id];
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('obj-card--destacado');
    setTimeout(() => el.classList.remove('obj-card--destacado'), 1500);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => { setLoading(false); }, 3000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    return () => { Object.values(timeoutsConclusaoRef.current).forEach(clearTimeout); };
  }, []);

  useEffect(() => {
    const handle = (e) => {
      const algumAberto = Object.entries(seletorEntidadeAberto).find(([, v]) => v);
      if (!algumAberto) return;
      const idx = algumAberto[0];
      const el = seletorEntidadeRef.current[idx];
      if (el && !el.contains(e.target)) {
        setSeletorEntidadeAberto(prev => ({ ...prev, [idx]: false }));
      }
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [seletorEntidadeAberto]);

  useEffect(() => {
    if (!usuarioId) return;
    supabase.from("profiles").select("nome").eq("id", usuarioId).single()
      .then(({ data }) => { if (data?.nome) setMeuNome(data.nome); });
  }, [usuarioId]);

  useEffect(() => {
    if (!containerAtual?.id) return;
    supabase.from("convites").select("user_id")
      .eq("container_id", containerAtual.id).eq("status", "aceito")
      .then(({ data }) => {
        setColaboradoresAtivosIds(new Set([containerAtual.id, ...(data || []).map(c => c.user_id).filter(Boolean)]));
      });
  }, [containerAtual?.id]);

  const sendNotification = useCallback(async (recipientUserId, message, type) => {
    if (!recipientUserId || !usuarioId) return;
    try {
      await supabase.from("notificacoes").insert([{
        user_id: recipientUserId, remetente_id: usuarioId, mensagem: message, tipo: type,
        lido: false, created_at: new Date().toISOString(),
        nota_id: notaAtual?.id || null, projeto_id: projetoAtual?.id || null,
        container_id: containerAtual?.id || null, pilha_id: notaAtual?.pilha_id || null,
        setor_id: projetoAtual?.setor_id || null,
      }]);
    } catch (err) { console.error("Erro ao enviar notificação:", err); }
  }, [usuarioId, notaAtual, projetoAtual, containerAtual]);

  const carregarComentarios = useCallback(async (objetivoId) => {
    if (!objetivoId || String(objetivoId).startsWith('temp')) return [];
    const { data, error } = await supabase.from("ata_objetivos_comentarios").select("*").eq("ata_objetivo_id", objetivoId).order("created_at", { ascending: true });
    if (error || !data?.length) return [];
    const userIds = [...new Set(data.map(c => c.usuario_id))];
    const { data: profiles } = await supabase.from("profiles").select("id, nome").in("id", userIds);
    const profilesMap = (profiles || []).reduce((acc, p) => { acc[p.id] = p.nome; return acc; }, {});
    return data.map(c => ({ ...c, profiles: { nome: profilesMap[c.usuario_id] || "Usuário" } }));
  }, []);

  const abrirComentarios = async (i, objetivoId) => {
    if (editandoComentario[i]) { setEditandoComentario(prev => ({ ...prev, [i]: false })); return; }
    if (comentariosObjetivo[objetivoId] !== undefined) { setEditandoComentario(prev => ({ ...prev, [i]: true })); return; }
    setCarregandoComentarios(prev => ({ ...prev, [i]: true }));
    const lista = await carregarComentarios(objetivoId);
    setComentariosObjetivo(prev => ({ ...prev, [objetivoId]: lista }));
    setCarregandoComentarios(prev => ({ ...prev, [i]: false }));
    setEditandoComentario(prev => ({ ...prev, [i]: true }));
  };

  const carregarObjetivos = useCallback(async () => {
    const myLoadId = ++loadIdRef.current;
    carregandoRef.current = true;

    if (!ataId) {
      setObjetivosList([]);
      setCriarObjetivos(false);
      carregandoRef.current = false;
      setLoading(false);
      return;
    }

    const { data: objetivosData, error: ataError } = await buscarObjetivosPaginado(ataId);

    if (ataError) {
      console.error("Erro ao carregar objetivos:", ataError);
      setObjetivosList([]);
      setCriarObjetivos(false);
      carregandoRef.current = false;
      setLoading(false);
      return;
    }

    const lista = Array.isArray(objetivosData) ? objetivosData : [];

    excluidosRef.current = new Set(
      lista.filter(o => o.texto?.startsWith(PREFIXO_EXCLUIDO)).map(o => o.texto.replace(PREFIXO_EXCLUIDO, '').trim().toLowerCase())
    );

    const objetivoIds = lista.map(o => o.id);
    let respPorObj = {};

    if (objetivoIds.length > 0) {
      const respData = await buscarEmLotes("ata_objetivos_responsaveis_enriquecidos", "ata_objetivo_id", objetivoIds);
      const userIds = [...new Set(respData.filter(r => r.usuario_id).map(r => r.usuario_id))];
      let avatarMap = {};
      if (userIds.length > 0) {
        const profilesData = await buscarEmLotes("profiles", "id", userIds, "id, avatar_url");
        avatarMap = profilesData.reduce((acc, p) => { acc[p.id] = p.avatar_url; return acc; }, {});
      }
      respPorObj = respData.reduce((acc, r) => {
        if (!acc[r.ata_objetivo_id]) acc[r.ata_objetivo_id] = [];
        acc[r.ata_objetivo_id].push({ id: r.id, usuario_id: r.usuario_id, nome_externo: r.nome_externo, nome_exibicao: r.nome_exibicao, avatar_url: avatarMap[r.usuario_id] || null });
        return acc;
      }, {});
    }

    const objetivos = lista.filter(o => !o.texto?.startsWith(PREFIXO_EXCLUIDO)).map(o => {
      const foiEnviado  = o.comentario?.startsWith('Objetivo enviado para:');
      const matchOrigem = o.comentario?.match(/\[ORIGEM:(\d+)\]/);
      return {
        id: o.id, texto: o.texto || "", responsaveis: respPorObj[o.id] || [],
        dataEntrega: o.data_entrega, concluido: o.concluido || false,
        concluidoEm: o.concluido_em ? new Date(o.concluido_em) : null,
        comentario: o.comentario || "", enviado: foiEnviado,
        ataDestinoNome: foiEnviado ? o.comentario.replace('Objetivo enviado para:', '').trim() : null,
        recebido: matchOrigem !== null, ataOrigemId: matchOrigem ? parseInt(matchOrigem[1]) : null,
        tarefasVinculadas: [],
        entidadesRelacionadas: [],
        origensChain: parseOrigemChain(o.comentario),
        criadoEm: o.created_at || null,
      };
    });

    const objetivoIds2 = objetivos.map(o => o.id).filter(id => id && !String(id).startsWith('temp'));
    if (objetivoIds2.length > 0) {
      const vinculos = await buscarEmLotes("ata_objetivos_notas_vinculadas", "ata_objetivo_id", objetivoIds2, "id, ata_objetivo_id, nota_id, notas(id, nome, tipo)");
      const vinculosMap = {};
      (vinculos || []).forEach(v => {
        if (!vinculosMap[v.ata_objetivo_id]) vinculosMap[v.ata_objetivo_id] = [];
        vinculosMap[v.ata_objetivo_id].push({ vinculoId: v.id, notaId: v.nota_id, nome: v.notas?.nome || "Tarefa vinculada", tipo: v.notas?.tipo || null });
      });
      objetivos.forEach(o => { o.tarefasVinculadas = vinculosMap[o.id] || []; });

      const entidadesVinc = await buscarEmLotes("ata_objetivos_entidades", "ata_objetivo_id", objetivoIds2);
      const entidadesMap = {};
      (entidadesVinc || []).forEach(e => {
        if (!entidadesMap[e.ata_objetivo_id]) entidadesMap[e.ata_objetivo_id] = [];
        entidadesMap[e.ata_objetivo_id].push({
          id: e.id, entidadeTipo: e.entidade_tipo, entidadeId: e.entidade_id,
          entidadeNome: e.entidade_nome, entidadeFoto: e.entidade_foto || null,
        });
      });
      objetivos.forEach(o => { o.entidadesRelacionadas = entidadesMap[o.id] || []; });
    }

    if (myLoadId !== loadIdRef.current) return;
    lastTextRef.current = "";

    setObjetivosList(objetivos);
    setCriarObjetivos(objetivos.length > 0);

    const objetivoIdsComentarios = objetivos.map(o => o.id).filter(id => id && !String(id).startsWith('temp'));
    if (objetivoIdsComentarios.length > 0) {
      const comentariosExistentes = await buscarEmLotes("ata_objetivos_comentarios", "ata_objetivo_id", objetivoIdsComentarios, "ata_objetivo_id, usuario_id");
      const contagem = {};
      const meus = new Set();
      (comentariosExistentes || []).forEach(c => {
        contagem[c.ata_objetivo_id] = (contagem[c.ata_objetivo_id] || 0) + 1;
        if (c.usuario_id === usuarioId) meus.add(c.ata_objetivo_id);
      });
      setComentariosCount(contagem);
      setRespondidosPorMim(meus);
    } else {
      setComentariosCount({});
      setRespondidosPorMim(new Set());
    }
    setComentariosObjetivo({});

    const anexosMap = {};
    const idsComAnexos = lista.filter(o => o.id && !String(o.id).startsWith('temp')).map(o => o.id);
    if (idsComAnexos.length > 0) {
      const anexosData = await buscarEmLotes("ata_objetivos_anexos", "ata_objetivo_id", idsComAnexos);
      for (const a of anexosData) {
        if (!anexosMap[a.ata_objetivo_id]) anexosMap[a.ata_objetivo_id] = [];
        anexosMap[a.ata_objetivo_id].push(a);
      }
    }
    setAnexosPorObjetivo(anexosMap);

    const reprogMap = {};
    if (idsComAnexos.length > 0) {
      const reprogData = await buscarEmLotes("ata_objetivos_reprogramacoes", "ata_objetivo_id", idsComAnexos);
      reprogData.sort((a, b) => new Date(b.alterado_em) - new Date(a.alterado_em));
      for (const r of reprogData) {
        if (!reprogMap[r.ata_objetivo_id]) reprogMap[r.ata_objetivo_id] = [];
        reprogMap[r.ata_objetivo_id].push(r);
      }
    }
    setReprogramacoesPorObjetivo(reprogMap);

    const atasOrigemIds = [...new Set(objetivos.filter(o => o.recebido && o.ataOrigemId).map(o => o.ataOrigemId))];
    if (atasOrigemIds.length > 0) {
      const atasOrigem = await buscarEmLotes("atas", "id", atasOrigemIds, "id, nota_id");
      if (atasOrigem?.length) {
        const notas = await buscarEmLotes("notas", "id", atasOrigem.map(a => a.nota_id).filter(Boolean), "id, nome");
        if (notas) {
          const nomesMap = {};
          atasOrigem.forEach(ata => { const nota = notas.find(n => n.id === ata.nota_id); if (nota) nomesMap[ata.id] = nota.nome; });
          setAtasOrigemNomes(nomesMap);
        }
      }
    }

    carregandoRef.current = false;
    setLoading(false);
    setLoadVersion(v => v + 1);
  }, [ataId, carregarComentarios, usuarioId]);

  useEffect(() => { carregarObjetivos(); lastTextRef.current = ""; }, [carregarObjetivos]);

  useEffect(() => {
    if (!criarObjetivos || isUpdatingRef.current || carregandoRef.current) return;
    if (lastTextRef.current === texto) return;
    lastTextRef.current = texto;
    isUpdatingRef.current = true;

    const objetivosSalvos = objetivosList.filter(o => o.id && !String(o.id).startsWith('temp'));
    if (!textoUsaAspasSimples(texto)) { setObjetivosList(objetivosSalvos); isUpdatingRef.current = false; return; }

    const validos = extrairObjetivosValidos(texto);
    const textosSalvosSet = new Set(objetivosSalvos.map(o => o.texto.toLowerCase().trim()));
    const temporariosAtuais = objetivosList.filter(o => String(o.id).startsWith('temp'))
      .filter(o => validos.some(txt => txt.toLowerCase().trim() === o.texto.toLowerCase().trim()));
    const textosTempExistentes = new Set(temporariosAtuais.map(o => o.texto.toLowerCase().trim()));
    const novosReais = validos
      .filter(txt => !textosSalvosSet.has(txt.toLowerCase().trim()) && !textosTempExistentes.has(txt.toLowerCase().trim()) && !excluidosRef.current.has(txt.toLowerCase().trim()))
      .map(txt => ({ id: `temp-${Date.now()}-${Math.random()}`, texto: txt, responsaveis: [], entidadesRelacionadas: [], dataEntrega: null, concluido: false, concluidoEm: null, comentario: "" }));

    setObjetivosList([...objetivosSalvos, ...temporariosAtuais, ...novosReais]);
    isUpdatingRef.current = false;
  }, [texto, criarObjetivos, loadVersion]);

  useEffect(() => {
    if (!ataId || !criarObjetivos || carregandoRef.current || !textoUsaAspasSimples(texto)) return;
    const timer = setTimeout(async () => {
      if (isSaving.current) return;
      isSaving.current = true;
      try {
        const objetivosSalvos = objetivosList.filter(o => o.id && !String(o.id).startsWith('temp'));
        const temporarios = objetivosList.filter(o => String(o.id).startsWith('temp'));
        if (!temporarios.length) return;

        const existentes = await buscarTextosExistentesPaginado(ataId);
        const novos = temporarios.filter(t => !existentes.has(t.texto.trim().toLowerCase()));
        if (!novos.length) return;

        console.log("[objetivos] inserindo", novos.length, "(de", temporarios.length, ") ata_id", ataId);
        const inserts = novos.map((obj, idx) => ({ ata_id: ataId, texto: obj.texto, concluido: false, comentario: "", data_entrega: null, concluido_em: null, ordem: objetivosSalvos.length + idx }));
        const inseridos = [];
        for (let c = 0; c < inserts.length; c += 50) {
          const { data, error } = await supabase.from("ata_objetivos").insert(inserts.slice(c, c + 50)).select();
          console.log("[objetivos] lote", c, "retornou", data?.length, "erro", error);
          if (error) { alert("Erro ao salvar objetivos: " + error.message); break; }
          inseridos.push(...(data || []));
        }
        const { count } = await supabase.from("ata_objetivos").select("id", { count: "exact", head: true }).eq("ata_id", ataId);
        console.log("[objetivos] inseridos", inseridos.length, "total no banco", count);
        if (inseridos.length) {
          setObjetivosList(prev => {
            const pool = [...inseridos];
            return prev.map(o => {
              if (!String(o.id).startsWith('temp')) return o;
              const k = pool.findIndex(n => n.texto === o.texto);
              if (k === -1) return o;
              const [novo] = pool.splice(k, 1);
              return { ...o, id: novo.id };
            });
          });
        }
      } finally { isSaving.current = false; }
    }, 800);
    return () => clearTimeout(timer);
  }, [texto, ataId, criarObjetivos, objetivosList, loadVersion]);

  const progressoPercent = objetivosList.length
    ? Math.round((objetivosList.filter(o => o.concluido).length / objetivosList.length) * 100) : 0;
  useEffect(() => { if (typeof onProgressoChange === "function") onProgressoChange(progressoPercent); }, [progressoPercent, onProgressoChange]);

  const todosResponsaveis = useMemo(() => {
    const seen = new Set(); const lista = [];
    for (const obj of objetivosList) {
      for (const resp of obj.responsaveis) {
        const key = resp.usuario_id || resp.nome_externo;
        if (key && !seen.has(key)) { seen.add(key); lista.push(resp); }
      }
    }
    return lista;
  }, [objetivosList]);

  const todasEntidades = useMemo(() => {
    const seen = new Set(); const lista = [];
    for (const obj of objetivosList) {
      for (const ent of (obj.entidadesRelacionadas || [])) {
        const key = `${ent.entidadeTipo}-${ent.entidadeId}`;
        if (!seen.has(key)) { seen.add(key); lista.push(ent); }
      }
    }
    return lista;
  }, [objetivosList]);

  // ── Autolimpeza: se o filtro salvo não existir na ata aberta, limpa ──
  useEffect(() => {
    if (loadVersion === 0) return;
    if (filtroResponsavel && !todosResponsaveis.some(r => (r.usuario_id || r.nome_externo) === filtroResponsavel)) {
      setFiltroResponsavel(null);
    }
    if (filtroEntidade && !todasEntidades.some(e => `${e.entidadeTipo}-${e.entidadeId}` === filtroEntidade)) {
      setFiltroEntidade(null);
    }
  }, [loadVersion, todosResponsaveis, todasEntidades, filtroResponsavel, filtroEntidade]);

  // ── Resumo do responsável selecionado no filtro ──
  const statsResponsavel = useMemo(() => {
    if (!filtroResponsavel) return null;
    const resp = todosResponsaveis.find(r => (r.usuario_id || r.nome_externo) === filtroResponsavel);
    if (!resp) return null;
    const meus = objetivosList.filter(o =>
      !o.enviado && o.responsaveis.some(r => (r.usuario_id || r.nome_externo) === filtroResponsavel)
    );
    const total = meus.length;
    const resolvidos = meus.filter(o => o.concluido).length;
    const abertos = total - resolvidos;
    const percentual = total ? Math.round((resolvidos / total) * 100) : 0;
    const maisAntigo = meus
      .filter(o => !o.concluido && o.criadoEm)
      .sort((a, b) => new Date(a.criadoEm) - new Date(b.criadoEm))[0] || null;
    const dias = maisAntigo ? Math.floor((Date.now() - new Date(maisAntigo.criadoEm)) / 86400000) : null;
    return { resp, total, resolvidos, abertos, percentual, maisAntigo, dias };
  }, [filtroResponsavel, todosResponsaveis, objetivosList]);

  // ── Resumo do projeto/setor selecionado no filtro ──
  const statsEntidade = useMemo(() => {
    if (!filtroEntidade) return null;
    const ent = todasEntidades.find(e => `${e.entidadeTipo}-${e.entidadeId}` === filtroEntidade);
    if (!ent) return null;
    const meus = objetivosList.filter(o =>
      !o.enviado && (o.entidadesRelacionadas || []).some(e => `${e.entidadeTipo}-${e.entidadeId}` === filtroEntidade)
    );
    const total = meus.length;
    const resolvidos = meus.filter(o => o.concluido).length;
    const abertos = total - resolvidos;
    const percentual = total ? Math.round((resolvidos / total) * 100) : 0;
    const maisAntigo = meus
      .filter(o => !o.concluido && o.criadoEm)
      .sort((a, b) => new Date(a.criadoEm) - new Date(b.criadoEm))[0] || null;
    const dias = maisAntigo ? Math.floor((Date.now() - new Date(maisAntigo.criadoEm)) / 86400000) : null;
    return { ent, total, resolvidos, abertos, percentual, maisAntigo, dias };
  }, [filtroEntidade, todasEntidades, objetivosList]);

  const objetivosFiltrados = useMemo(() => {
    let lista = objetivosList;

    if (filtroResponsavel) {
      lista = lista.filter(obj =>
        obj.responsaveis.some(r => (r.usuario_id && r.usuario_id === filtroResponsavel) || (!r.usuario_id && r.nome_externo === filtroResponsavel))
      );
    }
    if (filtroEntidade) {
      lista = lista.filter(obj => (obj.entidadesRelacionadas || []).some(e => `${e.entidadeTipo}-${e.entidadeId}` === filtroEntidade));
    }
    if (arquivarConcluidos) lista = lista.filter(obj => !obj.concluido || concluidosRecentes[obj.id]);
    if (soEmAberto)         lista = lista.filter(obj => (!obj.concluido && !respondidosPorMim.has(obj.id)) || concluidosRecentes[obj.id]);
    if (soSemResp)          lista = lista.filter(obj => obj.responsaveis.length === 0);

    const termo = buscaObjetivos.trim().toLowerCase();
    if (termo) lista = lista.filter(obj => obj.texto.toLowerCase().includes(termo));

    return lista;
  }, [objetivosList, filtroResponsavel, filtroEntidade, arquivarConcluidos, soEmAberto, soSemResp, buscaObjetivos, respondidosPorMim, concluidosRecentes]);

  const listaOrdenada = useMemo(() => {
    const base = [...objetivosFiltrados];
    const nomeProjeto = o => (o.entidadesRelacionadas?.[0]?.entidadeNome || "").toLowerCase();
    const nomeResp = o => (o.responsaveis[0]?.nome_exibicao || o.responsaveis[0]?.nome_externo || "").toLowerCase();
    const dataEnt = o => o.dataEntrega || "";
    const cmp = (a, b) => { if (!a && !b) return 0; if (!a) return 1; if (!b) return -1; return a.localeCompare(b); };
    if (ordenacao === 'recentes') return base.reverse();
    if (ordenacao === 'projeto') return base.sort((a, b) => cmp(nomeProjeto(a), nomeProjeto(b)));
    if (ordenacao === 'responsavel') return base.sort((a, b) => cmp(nomeResp(a), nomeResp(b)));
    if (ordenacao === 'data') return base.sort((a, b) => cmp(dataEnt(a), dataEnt(b)));
    return base;
  }, [objetivosFiltrados, ordenacao]);

  const criarObjetivoInlineAposIndex = (objetivoOrigem, index) => {
    const novaChain = [
      ...(objetivoOrigem.origensChain || []),
      { id: objetivoOrigem.id, texto: objetivoOrigem.texto },
    ];
    const tempId = `temp-inline-${Date.now()}-${Math.random()}`;
    const novoObjetivo = {
      id: tempId, texto: "", responsaveis: [], entidadesRelacionadas: [],
      dataEntrega: null, concluido: false, concluidoEm: null,
      comentario: buildOrigemChainTag(novaChain), tarefasVinculadas: [], origensChain: novaChain,
    };
    const novoIndex = index + 1;
    setObjetivosList(prev => {
      const novos = [...prev];
      novos.splice(novoIndex, 0, novoObjetivo);
      return novos;
    });
    setTextoTemp(prev => ({ ...prev, [novoIndex]: "" }));
    setEditandoTexto(prev => ({ ...prev, [novoIndex]: true }));
  };

  const salvarEdicaoObjetivo = async (i, novoTexto) => {
    const texto = novoTexto.trim();
    const objetivo = objetivosList[i];
    const ehInlineNovo = String(objetivo?.id).startsWith('temp-inline');

    if (!texto) {
      setEditandoTexto(prev => ({ ...prev, [i]: false }));
      if (ehInlineNovo) {
        setObjetivosList(prev => prev.filter((_, idx) => idx !== i));
        setTextoTemp(prev => { const { [i]: _, ...resto } = prev; return resto; });
      }
      return;
    }

    const novos = [...objetivosList];
    novos[i] = { ...objetivo, texto };
    setObjetivosList(novos);
    setEditandoTexto(prev => ({ ...prev, [i]: false }));

    if (ehInlineNovo) {
      const { data: item, error } = await supabase.from("ata_objetivos").insert({
        ata_id: ataId, texto, concluido: false, comentario: objetivo.comentario || "",
        data_entrega: null, concluido_em: null, ordem: objetivosList.length,
      }).select().single();
      if (!error && item) {
        setObjetivosList(prev => prev.map((o, idx) => (idx === i ? { ...o, id: item.id } : o)));
      } else {
        console.error(error);
        alert("Erro ao salvar novo objetivo.");
      }
      return;
    }

    if (objetivo?.id && !String(objetivo.id).startsWith('temp')) {
      const { error } = await supabase.from("ata_objetivos").update({ texto }).eq("id", objetivo.id);
      if (error) { console.error(error); alert("Erro ao salvar edição do objetivo."); }
    }
  };

  const toggleObjetivo = async (i) => {
    const objetivo = objetivosList[i];
    if (!objetivo?.id || !ataId || String(objetivo.id).startsWith('temp')) return;
    const novoConcluido = !objetivo.concluido;
    if (!novoConcluido && !podeDesmarcarConclusao(objetivo.concluidoEm)) { alert("Não é possível desmarcar um objetivo concluído há mais de 24 horas."); return; }
    const agora = new Date();
    const novos = [...objetivosList];
    novos[i] = { ...objetivo, concluido: novoConcluido, concluidoEm: novoConcluido ? agora : null };
    setObjetivosList(novos);
    try {
      const { error } = await supabase.from("ata_objetivos").update({
        concluido: novoConcluido, concluido_em: novoConcluido ? agora.toISOString() : null
      }).eq("id", objetivo.id);
      if (error) throw error;

      const novoProgresso = Math.round((novos.filter(o => o.concluido).length / novos.length) * 100);
      if (typeof onProgressoChange === "function") onProgressoChange(novoProgresso);
      if (notaAtual?.id) await supabase.from("notas").update({ progresso: novoProgresso }).eq("id", notaAtual.id);

      if (novoConcluido) {
        setMenuPosConclusao(i);
        if (soEmAberto || arquivarConcluidos) {
          setConcluidosRecentes(prev => ({ ...prev, [objetivo.id]: true }));
          clearTimeout(timeoutsConclusaoRef.current[objetivo.id]);
          timeoutsConclusaoRef.current[objetivo.id] = setTimeout(() => {
            setConcluidosRecentes(prev => { const { [objetivo.id]: _, ...resto } = prev; return resto; });
            delete timeoutsConclusaoRef.current[objetivo.id];
          }, 60000);
        }
        for (const resp of objetivo.responsaveis)
          if (resp.usuario_id) await sendNotification(resp.usuario_id, `Objetivo concluído: ${objetivo.texto}`, "objetivo_concluido");
      } else {
        setMenuPosConclusao(null);
      }
    } catch (err) {
      console.error("Erro ao salvar conclusão:", err);
      setObjetivosList([...objetivosList]);
      alert("Erro ao salvar estado do objetivo.");
    }
  };

  const abrirModalCriarTarefa = useCallback((objetivoIndex) => {
    setCriandoTarefaIndex(objetivoIndex);
    setTarefaView('entidades');
    setEntidadeSelecionadaTarefa(null);
    setPilhasDisponivelsTarefa([]);
    setBuscaTarefa("");
    carregarEntidadesParaTarefa();
  }, []);

  const carregarEntidadesContainer = useCallback(async () => {
    if (!containerAtual?.id) return [];
    setCarregandoEntidadesContainer(true);
    try {
      const { data: projetos } = await supabase.from("projects").select("id, name, photo_url").eq("user_id", containerAtual.id).eq("ativo", true).order("name", { ascending: true });
      const { data: setores } = await supabase.from("setores").select("id, name").eq("user_id", containerAtual.id).eq("ativo", true).order("name", { ascending: true });
      const lista = [
        ...(projetos || []).map(p => ({ id: p.id, nome: p.name, tipo: 'projeto', foto: p.photo_url || null })),
        ...(setores || []).map(s => ({ id: s.id, nome: s.name, tipo: 'setor', foto: null })),
      ];
      setEntidadesContainer(lista);
      return lista;
    } catch (err) {
      console.error("Erro ao carregar projetos/setores:", err);
      return [];
    } finally { setCarregandoEntidadesContainer(false); }
  }, [containerAtual?.id]);

  const carregarEntidadesParaTarefa = useCallback(async () => {
    if (!containerAtual?.id) return;
    setCarregandoPilhasTarefa(true);
    try {
      const lista = entidadesContainer || await carregarEntidadesContainer();
      setEntidadesDisponivelsTarefa(lista);
    } catch (err) { console.error("Erro ao carregar projetos/setores:", err); }
    finally { setCarregandoPilhasTarefa(false); }
  }, [containerAtual?.id, entidadesContainer, carregarEntidadesContainer]);

  const buscarPilhasDaEntidade = useCallback(async (entidade) => {
    setCarregandoPilhasTarefa(true);
    try {
      const pilhas = await buscarPilhasDaEntidadeComCompartilhadas(entidade);
      if (!pilhas.length) { alert("Nenhuma pilha encontrada neste projeto/setor. Crie uma pilha no Kanban primeiro."); return; }
      const ordenadas = [...pilhas].sort((a, b) => (a.title || "").localeCompare(b.title || ""));
      setPilhasDisponivelsTarefa(ordenadas.map(p => ({
        id: p.id,
        nome: p.tipo_pilha === 'compartilhada' ? `${p.title || "Pilha sem nome"} (compartilhada)` : (p.title || "Pilha sem nome"),
        tipo_pilha: p.tipo_pilha,
      })));
      setEntidadeSelecionadaTarefa(entidade);
      setTarefaView('pilhas');
    } catch (err) { console.error("Erro ao buscar pilhas:", err); alert("Erro ao buscar pilhas."); }
    finally { setCarregandoPilhasTarefa(false); }
  }, []);

  const fecharModalCriarTarefa = useCallback(() => {
    setCriandoTarefaIndex(null);
    setTarefaView('entidades');
    setEntidadeSelecionadaTarefa(null);
    setPilhasDisponivelsTarefa([]);
    setBuscaTarefa("");
  }, []);

  const entidadesFiltradas2 = useMemo(() => {
    const termo = buscaTarefa.trim().toLowerCase();
    if (!termo) return entidadesDisponivelsTarefa;
    return entidadesDisponivelsTarefa.filter(e => e.nome.toLowerCase().includes(termo));
  }, [entidadesDisponivelsTarefa, buscaTarefa]);

  const pilhasFiltradasBusca = useMemo(() => {
    const termo = buscaTarefa.trim().toLowerCase();
    if (!termo) return pilhasDisponivelsTarefa;
    return pilhasDisponivelsTarefa.filter(p => p.nome.toLowerCase().includes(termo));
  }, [pilhasDisponivelsTarefa, buscaTarefa]);

  const handleCriarTarefaDoObjetivo = async (objetivoOrigem, objetivoIndex) => {
    if (!notaAtual?.id) { alert("Nota não identificada para criar tarefa."); return; }
    abrirModalCriarTarefa(objetivoIndex);
  };

  const confirmarCriacaoTarefa = async (pilhaId) => {
    if (criandoTarefaIndex === null) return;
    const objetivo = objetivosList[criandoTarefaIndex];
    const index = criandoTarefaIndex;
    fecharModalCriarTarefa();
    try {
      const { data: nota } = await supabase.from("notas").select("container_id").eq("id", notaAtual.id).single();
      const { data: ataData } = await supabase.from("atas").select("nota_id").eq("id", ataId).single();
      let ataNome = "Ata desconhecida";
      if (ataData?.nota_id) {
        const { data: notaAta } = await supabase.from("notas").select("nome").eq("id", ataData.nota_id).single();
        ataNome = notaAta?.nome || "Ata desconhecida";
      }

      let projectoSetorInfo = projetoAtual?.tipo === 'setor'
        ? `Setor: ${projetoAtual.nome || projetoAtual.name}`
        : `Projeto: ${projetoAtual.nome || projetoAtual.name}`;

      const responsaveisNomes = objetivo.responsaveis.map(r => r.nome_exibicao || r.nickname || r.nome || "Usuário").join(", ");
      const entidadesNomes = (objetivo.entidadesRelacionadas || []).map(e => e.entidadeNome).join(", ");

      const descricao = `**Criado a partir de objetivo da ata**\n\n📋 Ata: ${ataNome}\n📂 ${projectoSetorInfo}\n👤 Responsáveis: ${responsaveisNomes || "Sem responsáveis"}${entidadesNomes ? `\n🏷️ Projetos/Setores relacionados: ${entidadesNomes}` : ""}`;

      const { data: novaNota, error } = await supabase.from("notas").insert({
        nome: objetivo.texto, pilha_id: pilhaId, container_id: nota.container_id,
        tipo: "Tarefas", created_by: usuarioId, descricao,
      }).select().single();
      if (error) throw error;

      if (objetivo.responsaveis.length > 0) {
        const respInsert = objetivo.responsaveis.filter(r => r.usuario_id).map(r => ({ nota_id: novaNota.id, usuario_id: r.usuario_id }));
        if (respInsert.length > 0) await supabase.from("nota_responsaveis").insert(respInsert);
      }

      if (objetivo.dataEntrega) await supabase.from("notas").update({ data_entrega: objetivo.dataEntrega }).eq("id", novaNota.id);

      const { data: novoVinculo } = await supabase.from("ata_objetivos_notas_vinculadas")
        .insert({ ata_objetivo_id: objetivo.id, nota_id: novaNota.id, criado_por: usuarioId })
        .select("id").single();

      setObjetivosList(prev => prev.map((o, i) =>
        i === index
          ? { ...o, tarefasVinculadas: [...(o.tarefasVinculadas || []), { vinculoId: novoVinculo.id, notaId: novaNota.id, nome: objetivo.texto, tipo: "Tarefas" }] }
          : o
      ));

      alert(`✅ Tarefa criada!\n\n📋 Ata: ${ataNome}\n👤 Responsáveis adicionados`);
    } catch (err) { console.error(err); alert("Erro ao criar tarefa."); }
  };

  const carregarEntidadesDisponiveis = useCallback(async () => {
    if (!containerAtual?.id) return;
    setCarregandoNotas(true);
    try {
      const lista = entidadesContainer || await carregarEntidadesContainer();
      setEntidadesDisponiveis(lista);
    } catch (err) { console.error("Erro ao carregar projetos/setores:", err); }
    finally { setCarregandoNotas(false); }
  }, [containerAtual?.id, entidadesContainer, carregarEntidadesContainer]);

  const buscarNotasDaEntidade = useCallback(async (entidade) => {
    if (!notaAtual?.id) return;
    setCarregandoNotas(true);
    try {
      const pilhas = await buscarPilhasDaEntidadeComCompartilhadas(entidade);
      if (!pilhas.length) {
        setNotasDisponiveis([]);
        setEntidadeSelecionada(entidade);
        setNotaLinkView('notas');
        return;
      }
      const pilhasMap = pilhas.reduce((acc, p) => {
        acc[p.id] = p.tipo_pilha === 'compartilhada' ? `${p.title || "Pilha sem nome"} (compartilhada)` : (p.title || "Pilha sem nome");
        return acc;
      }, {});
      const { data: notas, error } = await supabase.from("notas")
        .select("id, nome, tipo, pilha_id")
        .neq("id", notaAtual.id)
        .in("pilha_id", pilhas.map(p => p.id))
        .order("nome", { ascending: true });
      if (error) throw error;
      const notasComPilha = (notas || []).map(n => ({ ...n, pilhaNome: n.pilha_id ? (pilhasMap[n.pilha_id] || "Sem pilha") : "Sem pilha" }));
      setNotasDisponiveis(notasComPilha);
      setEntidadeSelecionada(entidade);
      setNotaLinkView('notas');
    } catch (err) { console.error("Erro ao buscar notas:", err); alert("Erro ao buscar notas."); }
    finally { setCarregandoNotas(false); }
  }, [notaAtual?.id]);

  const abrirModalLinkNota = useCallback((objetivoIndex) => {
    setLinkandoNotaIndex(objetivoIndex);
    setNotaLinkView('entidades');
    setEntidadeSelecionada(null);
    setNotasDisponiveis([]);
    setBuscaLinkNota("");
    carregarEntidadesDisponiveis();
  }, [carregarEntidadesDisponiveis]);

  const fecharModalLink = useCallback(() => {
    setLinkandoNotaIndex(null);
    setNotaLinkView('entidades');
    setEntidadeSelecionada(null);
    setNotasDisponiveis([]);
    setBuscaLinkNota("");
  }, []);

  const entidadesFiltradas = useMemo(() => {
    const termo = buscaLinkNota.trim().toLowerCase();
    if (!termo) return entidadesDisponiveis;
    return entidadesDisponiveis.filter(e => e.nome.toLowerCase().includes(termo));
  }, [entidadesDisponiveis, buscaLinkNota]);

  const notasFiltradasBusca = useMemo(() => {
    const termo = buscaLinkNota.trim().toLowerCase();
    if (!termo) return notasDisponiveis;
    return notasDisponiveis.filter(n => n.nome.toLowerCase().includes(termo));
  }, [notasDisponiveis, buscaLinkNota]);

  const linkarNotaAoObjetivo = async (notaSelecionada, objetivoIndex) => {
    const objetivo = objetivosList[objetivoIndex];
    if (!objetivo?.id || String(objetivo.id).startsWith('temp')) return;
    if (objetivo.tarefasVinculadas?.some(t => t.notaId === notaSelecionada.id)) {
      alert("Esta tarefa já está vinculada a este objetivo.");
      return;
    }
    try {
      const { data: novoVinculo, error } = await supabase.from("ata_objetivos_notas_vinculadas")
        .insert({ ata_objetivo_id: objetivo.id, nota_id: notaSelecionada.id, criado_por: usuarioId })
        .select("id").single();
      if (error) throw error;
      setObjetivosList(prev => prev.map((o, i) =>
        i === objetivoIndex
          ? { ...o, tarefasVinculadas: [...(o.tarefasVinculadas || []), { vinculoId: novoVinculo.id, notaId: notaSelecionada.id, nome: notaSelecionada.nome, tipo: notaSelecionada.tipo }] }
          : o
      ));
    } catch (err) { console.error("Erro ao linkar nota:", err); alert("Erro ao linkar nota ao objetivo."); }
  };

  const removerLinkNota = async (objetivoIndex, vinculoId) => {
    if (!window.confirm("Remover vínculo com esta tarefa?")) return;
    try {
      const { error } = await supabase.from("ata_objetivos_notas_vinculadas").delete().eq("id", vinculoId);
      if (error) throw error;
      setObjetivosList(prev => prev.map((o, i) =>
        i === objetivoIndex ? { ...o, tarefasVinculadas: o.tarefasVinculadas.filter(t => t.vinculoId !== vinculoId) } : o
      ));
    } catch (err) { console.error("Erro ao remover link:", err); alert("Erro ao remover vínculo."); }
  };

  const abrirSeletorEntidade = useCallback((i) => {
    setSeletorEntidadeAberto(prev => {
      const abrindo = !prev[i];
      return { ...Object.fromEntries(Object.keys(prev).map(k => [k, false])), [i]: abrindo };
    });
    setBuscaEntidadeObjetivo(prev => ({ ...prev, [i]: "" }));
    if (!entidadesContainer) carregarEntidadesContainer();
  }, [entidadesContainer, carregarEntidadesContainer]);

  const entidadesContainerFiltradas = useCallback((i) => {
    const termo = (buscaEntidadeObjetivo[i] || "").trim().toLowerCase();
    const lista = entidadesContainer || [];
    if (!termo) return lista;
    return lista.filter(e => e.nome.toLowerCase().includes(termo));
  }, [entidadesContainer, buscaEntidadeObjetivo]);

  const adicionarEntidadeAoObjetivo = async (i, entidade) => {
    const objetivo = objetivosList[i];
    if (objetivo?.concluido) return;
    if (objetivo?.entidadesRelacionadas?.some(e => e.entidadeId === entidade.id && e.entidadeTipo === entidade.tipo)) return;

    const novaEntidade = {
      id: Date.now() + Math.random(),
      entidadeTipo: entidade.tipo, entidadeId: entidade.id,
      entidadeNome: entidade.nome, entidadeFoto: entidade.foto || null,
    };
    const novos = [...objetivosList];
    novos[i] = { ...novos[i], entidadesRelacionadas: [...(novos[i].entidadesRelacionadas || []), novaEntidade] };
    setObjetivosList(novos);

    if (objetivo?.id && !String(objetivo.id).startsWith('temp')) {
      const { data, error } = await supabase.from("ata_objetivos_entidades").insert({
        ata_objetivo_id: objetivo.id, entidade_tipo: entidade.tipo, entidade_id: entidade.id,
        entidade_nome: entidade.nome, entidade_foto: entidade.foto || null, criado_por: usuarioId,
      }).select('id').single();
      if (!error && data?.id) {
        setObjetivosList(prev => {
          const u = [...prev];
          const idx = u[i].entidadesRelacionadas.findIndex(e => e.id === novaEntidade.id);
          if (idx !== -1) u[i].entidadesRelacionadas[idx] = { ...novaEntidade, id: data.id };
          return u;
        });
      } else if (error) {
        console.error("Erro ao vincular projeto/setor:", error);
        setObjetivosList(prev => prev.map((o, idx2) => idx2 === i ? { ...o, entidadesRelacionadas: o.entidadesRelacionadas.filter(e => e.id !== novaEntidade.id) } : o));
        alert("Erro ao vincular projeto/setor.");
      }
    }
  };

  const removerEntidadeDoObjetivo = async (entidade, i) => {
    const objetivo = objetivosList[i];
    if (objetivo?.concluido) return;
    const novos = [...objetivosList];
    novos[i] = { ...novos[i], entidadesRelacionadas: novos[i].entidadesRelacionadas.filter(e => e.id !== entidade.id) };
    setObjetivosList(novos);
    if (objetivo?.id && entidade.id && !String(objetivo.id).startsWith('temp'))
      await supabase.from("ata_objetivos_entidades").delete().eq("id", entidade.id);
  };

  const handleUploadAnexo = async (file, objetivoId) => {
    if (!file || !objetivoId) return;
    setUploadandoAnexo(prev => ({ ...prev, [objetivoId]: true }));
    try {
      const ext = file.name.split('.').pop();
      const path = `objetivos/${objetivoId}/${Date.now()}.${ext}`;
      const { error: uploadError } = await supabase.storage.from("ata-anexos").upload(path, file, { contentType: file.type, upsert: false });
      if (uploadError) throw uploadError;
      const { data: urlData } = supabase.storage.from("ata-anexos").getPublicUrl(path);
      const { data: anexo, error: dbError } = await supabase.from("ata_objetivos_anexos").insert({
        ata_objetivo_id: objetivoId, nome_arquivo: file.name, url: urlData.publicUrl,
        storage_path: path, tamanho: file.size, tipo_mime: file.type, enviado_por: usuarioId
      }).select().single();
      if (dbError) throw dbError;
      setAnexosPorObjetivo(prev => ({ ...prev, [objetivoId]: [...(prev[objetivoId] || []), anexo] }));
    } catch (err) { console.error("Erro no upload:", err); alert("Erro ao enviar arquivo."); }
    finally { setUploadandoAnexo(prev => ({ ...prev, [objetivoId]: false })); }
  };

  const handleRemoverAnexo = async (anexo, objetivoId) => {
    if (!window.confirm(`Remover "${anexo.nome_arquivo}"?`)) return;
    try {
      await supabase.storage.from("ata-anexos").remove([anexo.storage_path]);
      await supabase.from("ata_objetivos_anexos").delete().eq("id", anexo.id);
      setAnexosPorObjetivo(prev => ({ ...prev, [objetivoId]: prev[objetivoId].filter(a => a.id !== anexo.id) }));
    } catch (err) { console.error(err); alert("Erro ao remover arquivo."); }
  };

  const handleResponsavelInputChange = (e, i) => {
    const valor = e.target.value;
    setInputResponsavel(prev => ({ ...prev, [i]: valor }));
    if (objetivosList[i]?.concluido) return;
    if (valor.startsWith("@") && valor.length > 1 && containerAtual?.id) {
      const termo = valor.slice(1).toLowerCase();
      supabase.from("convites").select("user_id").eq("container_id", containerAtual.id).eq("status", "aceito")
        .then(async ({ data: convites, error: convitesError }) => {
          if (convitesError || !convites?.length) { setSugestoesResponsavel(prev => ({ ...prev, [i]: [] })); return; }
          const userIds = convites.map(c => c.user_id).filter(Boolean);
          const { data: profiles, error: profilesError } = await supabase.from("profiles").select("id, nickname, nome, funcao").in("id", userIds);
          if (profilesError) { setSugestoesResponsavel(prev => ({ ...prev, [i]: [] })); return; }
          const seen = new Set();
          const unicos = profiles.filter(p => p.nickname?.toLowerCase().includes(termo) || p.nome?.toLowerCase().includes(termo))
            .filter(p => { if (seen.has(p.id)) return false; seen.add(p.id); return true; }).slice(0, 10);
          setSugestoesResponsavel(prev => ({ ...prev, [i]: unicos }));
        }).catch(() => setSugestoesResponsavel(prev => ({ ...prev, [i]: [] })));
    } else { setSugestoesResponsavel(prev => ({ ...prev, [i]: [] })); }
  };

  const adicionarResponsavelExterno = async (nome, i) => {
    const objetivo = objetivosList[i];
    if (objetivo?.concluido) return;
    if (objetivo?.responsaveis.some(r => r.nome_externo === nome && !r.usuario_id)) return;
    const novoResp = { nome_externo: nome, usuario_id: null, nome_exibicao: nome, id: Date.now() + Math.random() };
    const novos = [...objetivosList];
    novos[i] = { ...novos[i], responsaveis: [...novos[i].responsaveis, novoResp] };
    setObjetivosList(novos);
    if (objetivo?.id && !String(objetivo.id).startsWith('temp')) {
      const { data, error } = await supabase.from("ata_objetivos_responsaveis").insert({ ata_objetivo_id: objetivo.id, nome_externo: nome, usuario_id: null }).select('id');
      if (error) {
        console.error("Erro ao salvar responsável:", error);
        alert("Erro ao salvar responsável: " + error.message);
        setObjetivosList(prev => prev.map((o, idx2) => idx2 === i ? { ...o, responsaveis: o.responsaveis.filter(r => r.id !== novoResp.id) } : o));
      } else if (data?.[0]?.id) {
        setObjetivosList(prev => { const u = [...prev]; const idx = u[i].responsaveis.findIndex(r => r.id === novoResp.id); if (idx !== -1) u[i].responsaveis[idx] = { ...novoResp, id: data[0].id }; return u; });
      }
    }
  };

  const adicionarResponsavelInterno = async (item, i) => {
    const objetivo = objetivosList[i];
    if (objetivo?.concluido) return;
    if (objetivo?.responsaveis.some(r => r.usuario_id === item.id)) return;
    const { data: profileData } = await supabase.from("profiles").select("avatar_url").eq("id", item.id).single();
    const novoResp = { id: Date.now() + Math.random(), usuario_id: item.id, nome: item.nome, nickname: item.nickname, nome_exibicao: item.nome, avatar_url: profileData?.avatar_url || null };
    const novos = [...objetivosList];
    novos[i] = { ...novos[i], responsaveis: [...novos[i].responsaveis, novoResp] };
    setObjetivosList(novos);
    setInputResponsavel(prev => ({ ...prev, [i]: "" }));
    setSugestoesResponsavel(prev => ({ ...prev, [i]: [] }));
    if (objetivo?.id && !String(objetivo.id).startsWith('temp')) {
      const { data, error } = await supabase.from("ata_objetivos_responsaveis").insert({ ata_objetivo_id: objetivo.id, usuario_id: item.id, nome_externo: null }).select('id');
      if (error) {
        console.error("Erro ao salvar responsável:", error);
        alert("Erro ao salvar responsável: " + error.message);
        setObjetivosList(prev => prev.map((o, idx2) => idx2 === i ? { ...o, responsaveis: o.responsaveis.filter(r => r.id !== novoResp.id) } : o));
      } else if (data?.[0]?.id) {
        setObjetivosList(prev => { const u = [...prev]; const idx = u[i].responsaveis.findIndex(r => r.id === novoResp.id); if (idx !== -1) u[i].responsaveis[idx] = { ...novoResp, id: data[0].id }; return u; });
        await sendNotification(item.id, `Você foi designado responsável por: ${objetivo.texto}`, "objetivo_responsavel");
      }
    }
  };

  const removerResponsavel = async (responsavel, i) => {
    const objetivo = objetivosList[i];
    if (objetivo?.concluido) return;
    const novos = [...objetivosList];
    novos[i] = { ...novos[i], responsaveis: novos[i].responsaveis.filter(r => r.id !== responsavel.id) };
    setObjetivosList(novos);
    if (objetivo?.id && responsavel.id && !String(objetivo.id).startsWith('temp'))
      await supabase.from("ata_objetivos_responsaveis").delete().eq("id", responsavel.id);
  };

  const removerObjetivo = async (i) => {
    const objetivo = objetivosList[i];
    if (objetivo?.concluido) { alert("Não é possível excluir um objetivo concluído."); return; }
    const novos = [...objetivosList];
    novos.splice(i, 1);
    setObjetivosList(novos);
    excluidosRef.current.add(objetivo.texto.trim().toLowerCase());
    if (objetivo?.id && !String(objetivo.id).startsWith('temp'))
      await supabase.from("ata_objetivos").update({ texto: `${PREFIXO_EXCLUIDO} ${objetivo.texto}` }).eq("id", objetivo.id);
  };

  const buscarAtasDisponiveis = useCallback(async (objetivoIndex) => {
    if (!ataId || !projetoAtual?.id) return;
    try {
      const pilhas = await buscarPilhasDaEntidadeComCompartilhadas({
        tipo: projetoAtual.tipo === 'projeto' ? 'projeto' : 'setor',
        id: projetoAtual.id,
      });
      if (!pilhas?.length) { alert("Não há pilhas disponíveis."); return; }
      const { data: notasAtas, error: erroNotas } = await supabase.from("notas").select("id, nome, pilha_id, progresso").eq("tipo", "Atas").in("pilha_id", pilhas.map(p => p.id));
      if (erroNotas) throw erroNotas;
      const notasDisponiveis = (notasAtas || []).filter(n => n.id !== notaAtual?.id && (n.progresso == null || n.progresso < 100));
      if (!notasDisponiveis.length) { alert("Não há outras atas disponíveis."); return; }
      const { data: atas, error: atasError } = await supabase.from("atas").select("id, pauta, nota_id").in("nota_id", notasDisponiveis.map(n => n.id));
      if (atasError) throw atasError;
      setAtasDisponiveis((atas || []).map(ata => {
        const nota = notasDisponiveis.find(n => n.id === ata.nota_id);
        return { ataId: ata.id, notaId: ata.nota_id, nome: nota?.nome || "Ata sem nome", pauta: ata.pauta || "Sem pauta", progresso: nota?.progresso || 0 };
      }));
      setMostrarAtasDisponiveis(prev => ({ ...prev, [objetivoIndex]: true }));
    } catch (err) { console.error("Erro ao buscar atas:", err); alert("Erro ao carregar atas."); }
  }, [ataId, projetoAtual, notaAtual]);

  const realocarObjetivo = async (objetivoIndex, ataDestinoId, ataDestinoNome) => {
    const objetivo = objetivosList[objetivoIndex];
    if (!objetivo?.id || String(objetivo.id).startsWith('temp')) { alert("Objetivo não salvo."); return; }
    if (objetivo.concluido) { alert("Não é possível realocar um objetivo concluído."); return; }
    try {
      const comentarioDestino = objetivo.origensChain?.length
        ? `[ORIGEM:${ataId}]${buildOrigemChainTag(objetivo.origensChain)}`
        : `[ORIGEM:${ataId}]`;

      const { data: objetivoInserido, error: erroInsert } = await supabase.from("ata_objetivos")
        .insert([{ ata_id: ataDestinoId, texto: objetivo.texto, concluido: false, comentario: comentarioDestino, data_entrega: objetivo.dataEntrega, concluido_em: null, ordem: 0 }]).select().single();
      if (erroInsert) throw erroInsert;
      if (objetivo.responsaveis.length > 0)
        await supabase.from("ata_objetivos_responsaveis").insert(objetivo.responsaveis.map(r => ({ ata_objetivo_id: objetivoInserido.id, usuario_id: r.usuario_id || null, nome_externo: r.nome_externo || null })));
      if (objetivo.entidadesRelacionadas?.length > 0)
        await supabase.from("ata_objetivos_entidades").insert(objetivo.entidadesRelacionadas.map(e => ({ ata_objetivo_id: objetivoInserido.id, entidade_tipo: e.entidadeTipo, entidade_id: e.entidadeId, entidade_nome: e.entidadeNome, entidade_foto: e.entidadeFoto || null, criado_por: usuarioId })));

      const comentariosOrigem = comentariosObjetivo[objetivo.id] !== undefined ? comentariosObjetivo[objetivo.id] : await carregarComentarios(objetivo.id);
      if (comentariosOrigem?.length > 0) {
        await supabase.from("ata_objetivos_comentarios").insert(
          comentariosOrigem.filter(c => !String(c.id).startsWith('temp-')).map(c => ({ ata_objetivo_id: objetivoInserido.id, usuario_id: c.usuario_id, comentario: c.comentario }))
        );
      }

      const anexosOrigem = anexosPorObjetivo[objetivo.id] || [];
      if (anexosOrigem.length > 0) {
        await supabase.from("ata_objetivos_anexos").insert(anexosOrigem.map(a => ({
          ata_objetivo_id: objetivoInserido.id, nome_arquivo: a.nome_arquivo, url: a.url,
          storage_path: a.storage_path, tamanho: a.tamanho, tipo_mime: a.tipo_mime, enviado_por: a.enviado_por,
        })));
      }

      if (objetivo.tarefasVinculadas?.length > 0) {
        await supabase.from("ata_objetivos_notas_vinculadas").insert(
          objetivo.tarefasVinculadas.map(t => ({ ata_objetivo_id: objetivoInserido.id, nota_id: t.notaId, criado_por: usuarioId }))
        );
      }

      const agora = new Date();
      const sufixoOrigem = objetivo.origensChain?.length ? buildOrigemChainTag(objetivo.origensChain) : "";
      const comentarioEnviado = `Objetivo enviado para: ${ataDestinoNome}${sufixoOrigem}`;
      await supabase.from("ata_objetivos").update({ concluido: true, concluido_em: agora.toISOString(), comentario: comentarioEnviado }).eq("id", objetivo.id);
      const novosObjetivos = [...objetivosList];
      novosObjetivos[objetivoIndex] = { ...objetivo, concluido: true, concluidoEm: agora, comentario: comentarioEnviado, enviado: true, ataDestinoNome };
      setObjetivosList(novosObjetivos);
      const novoProgresso = Math.round((novosObjetivos.filter(o => o.concluido).length / novosObjetivos.length) * 100);
      if (typeof onProgressoChange === "function") onProgressoChange(novoProgresso);
      if (notaAtual?.id) await supabase.from("notas").update({ progresso: novoProgresso }).eq("id", notaAtual.id);
      setMostrarAtasDisponiveis(prev => ({ ...prev, [objetivoIndex]: false }));
      alert("Objetivo realocado com sucesso!");
    } catch (err) { console.error("Erro ao realocar:", err); alert("Erro ao realocar objetivo."); }
  };

  const removerComentario = async (comentario, objetivoId) => {
    if (!comentario?.id || String(comentario.id).startsWith('temp-')) return;
    if (comentario.usuario_id !== usuarioId) return;
    if (!window.confirm("Excluir este comentário?")) return;
    try {
      const { error } = await supabase.from("ata_objetivos_comentarios").delete().eq("id", comentario.id);
      if (error) throw error;
      setComentariosObjetivo(prev => ({ ...prev, [objetivoId]: prev[objetivoId].filter(c => c.id !== comentario.id) }));
      setComentariosCount(prev => ({ ...prev, [objetivoId]: Math.max(0, (prev[objetivoId] || 1) - 1) }));
    } catch (err) { console.error("Erro ao excluir comentário:", err); alert("Erro ao excluir comentário."); }
  };

  const enviarComentario = async (i) => {
    const comentario = comentarioTemp[i]?.trim();
    if (!comentario) return;
    const objetivo = objetivosList[i];
    if (!objetivo?.id || !usuarioId || String(objetivo.id).startsWith('temp')) return;
    if (enviandoComentario[i]) return;
    setEnviandoComentario(prev => ({ ...prev, [i]: true }));
    const tempId = `temp-${Date.now()}`;
    setComentariosObjetivo(prev => ({ ...prev, [objetivo.id]: [...(prev[objetivo.id] || []), { id: tempId, ata_objetivo_id: objetivo.id, usuario_id: usuarioId, comentario, created_at: new Date().toISOString(), profiles: { nome: meuNome } }] }));
    setComentarioTemp(prev => ({ ...prev, [i]: "" }));
    try {
      const { data, error } = await supabase.from("ata_objetivos_comentarios").insert({ ata_objetivo_id: objetivo.id, usuario_id: usuarioId, comentario }).select().single();
      if (error) throw error;
      const { data: profile } = await supabase.from("profiles").select("nome").eq("id", usuarioId).single();
      setComentariosObjetivo(prev => ({ ...prev, [objetivo.id]: prev[objetivo.id].map(c => c.id === tempId ? { ...data, profiles: { nome: profile?.nome || meuNome } } : c) }));
      setComentariosCount(prev => ({ ...prev, [objetivo.id]: (prev[objetivo.id] || 0) + 1 }));
      for (const resp of objetivo.responsaveis)
        if (resp.usuario_id && resp.usuario_id !== usuarioId)
          await sendNotification(resp.usuario_id, `Novo comentário em objetivo: ${objetivo.texto}`, "objetivo_comentario");
    } catch (err) {
      console.error("Erro ao enviar comentário:", err);
      setComentariosObjetivo(prev => ({ ...prev, [objetivo.id]: prev[objetivo.id].filter(c => c.id !== tempId) }));
      setComentarioTemp(prev => ({ ...prev, [i]: comentario }));
      alert("Erro ao enviar comentário.");
    } finally { setEnviandoComentario(prev => ({ ...prev, [i]: false })); }
  };

  const handleCriarObjetivosChange = (e) => {
    const novaEscolha = e.target.checked;
    if (!novaEscolha && objetivosList.some(o => o.id && !String(o.id).startsWith('temp'))) { alert("Exclua todos os objetivos primeiro."); return; }
    setCriarObjetivos(novaEscolha);
    if (!novaEscolha) setObjetivosList([]);
  };

  useEffect(() => {
    if (!criarObjetivosExterno && criarObjetivos) {
      handleCriarObjetivosChange({ target: { checked: false } });
    }
  }, [criarObjetivosExterno]);

  const handleDragEnd = async (result) => {
    if (ordenacao !== 'recentes' && ordenacao !== 'antigos') return;
    if (!result.destination || result.source.index === result.destination.index) return;
    const listaBase = filtroResponsavel ? objetivosFiltrados : objetivosList;
    const n = listaBase.length;
    const sourceIdx = ordemDesc ? (n - 1 - result.source.index) : result.source.index;
    const destIdx   = ordemDesc ? (n - 1 - result.destination.index) : result.destination.index;
    const reordenada = Array.from(listaBase);
    const [moved] = reordenada.splice(sourceIdx, 1);
    reordenada.splice(destIdx, 0, moved);
    let novaLista;
    if (filtroResponsavel) {
      novaLista = [...objetivosList];
      let ri = 0;
      for (let idx = 0; idx < novaLista.length; idx++) {
        if (objetivosFiltrados.some(f => f.id === novaLista[idx].id)) novaLista[idx] = reordenada[ri++];
      }
    } else { novaLista = reordenada; }
    setObjetivosList(novaLista);
    isUpdatingRef.current = true; lastTextRef.current = texto;
    try {
      const updates = novaLista.map((obj, idx) => ({ id: obj.id, ordem: idx })).filter(o => o.id && !String(o.id).startsWith('temp'));
      if (updates.length > 0) await Promise.all(updates.map(u => supabase.from("ata_objetivos").update({ ordem: u.ordem }).eq("id", u.id)));
    } catch (err) { console.error("Erro ao salvar ordem:", err); }
    finally { setTimeout(() => { isUpdatingRef.current = false; }, 100); }
  };

  if (loading) {
    return <AtaObjetivosSkeleton />;
  }

  return (
    <>
      {criarObjetivos && (
        <div className="ata-section">
          <div className="ata-objectives">

            <div className="objetivos-header">
              <div className="objetivos-header-titulo">
                <FontAwesomeIcon icon={faTasks} />
                <span>Lista de objetivos</span>
                <span className="objetivos-contador">
                  ({objetivosList.length} · {objetivosList.filter(o => o.concluido).length} concluídos)
                </span>
              </div>
              <select
                value={ordenacao}
                onChange={e => setOrdenacao(e.target.value)}
                title="Ordenar objetivos"
                style={{ fontSize: 11, padding: '2px 4px', borderRadius: 4, border: '1px solid transparent', background: 'transparent', color: '#9ca3af', cursor: 'pointer', outline: 'none', maxWidth: 130 }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#e5e7eb'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'transparent'; }}
              >
                <option value="recentes">Recentes primeiro</option>
                <option value="antigos">Antigos primeiro</option>
                <option value="projeto">Por projeto/setor</option>
                <option value="responsavel">Por responsável</option>
                <option value="data">Por data de entrega</option>
              </select>
            </div>

            <div className="progress-segmentada-wrap">
              <div className="progress-segmentada">
                {objetivosList.map(o => (
                  <div key={o.id} className={`progress-segmento ${o.concluido ? 'preenchido' : ''}`} title={o.texto} />
                ))}
                {objetivosList.length === 0 && <div className="progress-segmento progress-segmento--vazio" />}
              </div>
              <span className="progress-segmentada-percent">{progressoPercent}%</span>
            </div>

            {/* ── Resumo do responsável selecionado no filtro ── */}
            {statsResponsavel && (
              <div className="resp-resumo">
                <div className="resp-resumo-topo">
                  <div className="resp-resumo-avatar">
                    {statsResponsavel.resp.avatar_url ? (
                      <img
                        src={statsResponsavel.resp.avatar_url}
                        alt={statsResponsavel.resp.nome_exibicao}
                        onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                      />
                    ) : null}
                    <div className="resp-resumo-avatar-iniciais" style={{ display: statsResponsavel.resp.avatar_url ? 'none' : 'flex' }}>
                      {gerarAbreviacao(statsResponsavel.resp.nome_exibicao || statsResponsavel.resp.nome_externo)}
                    </div>
                  </div>
                  <div className="resp-resumo-nome">
                    {statsResponsavel.resp.nome_exibicao || statsResponsavel.resp.nome_externo}
                  </div>
                  <div className="resp-resumo-percent">{statsResponsavel.percentual}%</div>
                </div>

                <div className="resp-resumo-barra">
                  <div className="resp-resumo-barra-fill" style={{ width: `${statsResponsavel.percentual}%` }} />
                </div>

                <div className="resp-resumo-numeros">
                  <div className="resp-resumo-num">
                    <strong>{statsResponsavel.total}</strong>
                    <span>Total</span>
                  </div>
                  <div className="resp-resumo-num resp-resumo-num--aberto">
                    <strong>{statsResponsavel.abertos}</strong>
                    <span>Em aberto</span>
                  </div>
                  <div className="resp-resumo-num resp-resumo-num--resolvido">
                    <strong>{statsResponsavel.resolvidos}</strong>
                    <span>Resolvidos</span>
                  </div>
                </div>

                {statsResponsavel.maisAntigo && (
                  <div
                    className="resp-resumo-antigo"
                    onClick={() => scrollParaObjetivo(statsResponsavel.maisAntigo.id)}
                    title="Ir até o objetivo"
                  >
                    <span className="resp-resumo-antigo-label">
                      Aberto há mais tempo · {statsResponsavel.dias === 0 ? 'hoje' : statsResponsavel.dias === 1 ? 'há 1 dia' : `há ${statsResponsavel.dias} dias`}
                    </span>
                    <span className="resp-resumo-antigo-texto">{statsResponsavel.maisAntigo.texto}</span>
                  </div>
                )}
              </div>
            )}

            {/* ── Resumo do projeto/setor selecionado no filtro ── */}
            {statsEntidade && (
              <div className="resp-resumo resp-resumo--entidade">
                <div className="resp-resumo-topo">
                  <div className="resp-resumo-avatar resp-resumo-avatar--entidade">
                    {statsEntidade.ent.entidadeFoto && statsEntidade.ent.entidadeTipo === 'projeto' ? (
                      <img
                        src={statsEntidade.ent.entidadeFoto}
                        alt={statsEntidade.ent.entidadeNome}
                        onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                      />
                    ) : null}
                    <div
                      className="resp-resumo-avatar-iniciais"
                      style={{ display: statsEntidade.ent.entidadeFoto && statsEntidade.ent.entidadeTipo === 'projeto' ? 'none' : 'flex' }}
                    >
                      {gerarAbreviacao(statsEntidade.ent.entidadeNome)}
                    </div>
                  </div>
                  <div className="resp-resumo-nome">
                    {statsEntidade.ent.entidadeNome}
                    <small className="resp-resumo-tipo">{statsEntidade.ent.entidadeTipo === 'projeto' ? 'Projeto' : 'Setor'}</small>
                  </div>
                  <div className="resp-resumo-percent">{statsEntidade.percentual}%</div>
                </div>

                <div className="resp-resumo-barra">
                  <div className="resp-resumo-barra-fill" style={{ width: `${statsEntidade.percentual}%` }} />
                </div>

                <div className="resp-resumo-numeros">
                  <div className="resp-resumo-num">
                    <strong>{statsEntidade.total}</strong>
                    <span>Total</span>
                  </div>
                  <div className="resp-resumo-num resp-resumo-num--aberto">
                    <strong>{statsEntidade.abertos}</strong>
                    <span>Em aberto</span>
                  </div>
                  <div className="resp-resumo-num resp-resumo-num--resolvido">
                    <strong>{statsEntidade.resolvidos}</strong>
                    <span>Resolvidos</span>
                  </div>
                </div>

                {statsEntidade.maisAntigo && (
                  <div
                    className="resp-resumo-antigo"
                    onClick={() => scrollParaObjetivo(statsEntidade.maisAntigo.id)}
                    title="Ir até o objetivo"
                  >
                    <span className="resp-resumo-antigo-label">
                      Aberto há mais tempo · {statsEntidade.dias === 0 ? 'hoje' : statsEntidade.dias === 1 ? 'há 1 dia' : `há ${statsEntidade.dias} dias`}
                    </span>
                    <span className="resp-resumo-antigo-texto">{statsEntidade.maisAntigo.texto}</span>
                  </div>
                )}
              </div>
            )}

            <div className="objetivos-scroll">
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="objetivos-list">
                  {(provided) => (
                    <div className="objetivos-list-inner" ref={provided.innerRef} {...provided.droppableProps}>
                      {listaOrdenada.map((o, filtroIdx) => {
                        const i = objetivosList.findIndex(obj => obj.id === o.id);
                        if (i === -1) return null;
                        const textoCapitalizado = o.texto.charAt(0).toUpperCase() + o.texto.slice(1);
                        const isConcluido   = o.concluido;
                        const podeDesmarcar = podeDesmarcarConclusao(o.concluidoEm);
                        const comentarios   = comentariosObjetivo[o.id] || [];
                        const numComentarios = comentariosObjetivo[o.id] !== undefined ? comentarios.length : (comentariosCount[o.id] || 0);
                        const hasComments   = numComentarios > 0;
                        const showMenuPos   = menuPosConclusao === i;

                        return (
                          <Draggable key={String(o.id)} draggableId={String(o.id)} index={filtroIdx} isDragDisabled={isConcluido || (ordenacao !== 'recentes' && ordenacao !== 'antigos')}>
                            {(provided, snapshot) => (
                              <div
                                ref={el => { provided.innerRef(el); objetivoRefs.current[o.id] = el; }}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                className={`obj-card ${isConcluido ? (o.enviado ? 'obj-card--enviado' : 'obj-card--concluido') : (o.recebido ? 'obj-card--recebido' : '')} ${snapshot.isDragging ? 'obj-card--dragging' : ''}`}
                                style={{ ...provided.draggableProps.style, opacity: snapshot.isDragging ? 0.85 : 1 }}
                              >
                                <div className="obj-linha1">
                                  <input
                                    type="checkbox"
                                    checked={isConcluido}
                                    onChange={() => toggleObjetivo(i)}
                                    disabled={isConcluido && !podeDesmarcar}
                                    onClick={e => e.stopPropagation()}
                                    className="obj-checkbox"
                                  />
                                  <span className={`obj-texto ${isConcluido ? 'obj-texto--concluido' : ''}`}>
                                    <strong className="obj-num">{i + 1}.</strong>{' '}
                                    {editandoTexto[i] ? (
                                      <input
                                        type="text"
                                        className="obj-texto-edit-input"
                                        autoFocus
                                        value={textoTemp[i] ?? o.texto}
                                        onChange={e => setTextoTemp(prev => ({ ...prev, [i]: e.target.value }))}
                                        onClick={e => e.stopPropagation()}
                                        onBlur={() => salvarEdicaoObjetivo(i, textoTemp[i] ?? o.texto)}
                                        onKeyDown={e => {
                                          if (e.key === "Enter") { e.preventDefault(); e.target.blur(); }
                                          if (e.key === "Escape") setEditandoTexto(prev => ({ ...prev, [i]: false }));
                                        }}
                                      />
                                    ) : renderTextoFormatado(textoCapitalizado)}
                                    {o.enviado && o.ataDestinoNome && (
                                      <div className="obj-tag obj-tag--enviado">↗ Enviado para: {o.ataDestinoNome}</div>
                                    )}
                                    {o.recebido && o.ataOrigemId && atasOrigemNomes[o.ataOrigemId] && (
                                      <div className="obj-tag obj-tag--recebido">↙ Vindo da ata: {atasOrigemNomes[o.ataOrigemId]}</div>
                                    )}
                                    {o.origensChain?.length > 0 && (
                                      <div className="obj-tag obj-tag--origem">
                                        Histórico de origem:
                                        {o.origensChain.map((h, idx) => (
                                          <div
                                            key={idx}
                                            className={`obj-origem-item ${h.id ? '' : 'obj-origem-item--legado'}`}
                                            onClick={h.id ? (e => { e.stopPropagation(); scrollParaObjetivo(h.id); }) : undefined}
                                            title={h.id ? "Clique para ir até o objetivo original" : "Objetivo original não identificado (dado antigo)"}
                                          >
                                            <em>"{h.texto}"</em>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </span>

                                  <div className="obj-acoes-dir">
                                    {isConcluido && (
                                      <label className="obj-icon-acao" title="Anexar arquivo">
                                        {uploadandoAnexo[o.id] ? <span className="spinner-comentario" /> : (
                                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
                                          </svg>
                                        )}
                                        <input type="file" style={{ display: 'none' }}
                                          ref={el => fileInputRefs.current[o.id] = el}
                                          onChange={e => { if (e.target.files[0]) handleUploadAnexo(e.target.files[0], o.id); e.target.value = ''; }}
                                        />
                                      </label>
                                    )}

                                    <div style={{ position: 'relative' }}>
                                      <ComentarioIcon
                                        onClick={() => abrirComentarios(i, o.id)}
                                        title={hasComments ? "Ver comentários" : "Comentar"}
                                        hasComments={hasComments}
                                        isLoading={!!carregandoComentarios[i]}
                                        count={numComentarios}
                                      />
                                      {editandoComentario[i] && (
                                        <div className="comentario-chat-container">
                                          <div className="comentario-input-area">
                                            <input type="text" value={comentarioTemp[i] || ""} onChange={e => setComentarioTemp(prev => ({ ...prev, [i]: e.target.value }))} placeholder="Adicionar comentário..." className="comentario-input" disabled={enviandoComentario[i]}
                                              onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey && !enviandoComentario[i]) { e.preventDefault(); enviarComentario(i); } }} />
                                            <button onClick={() => enviarComentario(i)} className="btn-enviar-comentario" disabled={!comentarioTemp[i]?.trim() || enviandoComentario[i]}>
                                              {enviandoComentario[i] ? <span className="spinner-comentario" /> : <FontAwesomeIcon icon={faPaperPlane} />}
                                            </button>
                                          </div>
                                          {comentarios.length > 0 && (
                                            <div className="comentarios-lista">
                                              {[...comentarios].reverse().map((c, idx) => {
                                                const isTemp = String(c.id).startsWith('temp-');
                                                const souAutor = c.usuario_id === usuarioId;
                                                return (
                                                  <div key={`${c.id}-${idx}`} className={`comentario-item ${isTemp ? 'comentario-temporario' : ''}`}>
                                                    <div className="comentario-item-header">
                                                      <span className="comentario-autor">{c.profiles?.nome || "Usuário"}</span>
                                                      {souAutor && !isTemp && (
                                                        <button className="comentario-excluir-btn" onClick={() => removerComentario(c, o.id)} title="Excluir comentário">×</button>
                                                      )}
                                                    </div>
                                                    <div className="comentario-texto">{c.comentario}</div>
                                                    <div className="comentario-data">
                                                      {isTemp
                                                        ? <span style={{ fontStyle: 'italic', color: '#9ca3af' }}>Enviando...</span>
                                                        : new Date(c.created_at).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                                    </div>
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>

                                    <span
                                      className="obj-icon-btn"
                                      style={{ fontSize: '14px' }}
                                      title="Criar tarefa a partir deste objetivo"
                                      onClick={e => { e.stopPropagation(); handleCriarTarefaDoObjetivo(o, i); }}
                                    >
                                      <FontAwesomeIcon icon={faTasks} />
                                    </span>

                                    <LinkNotaIcon
                                      onClick={() => abrirModalLinkNota(i)}
                                      title={o.tarefasVinculadas?.length > 0 ? "Gerenciar vínculos" : "Vincular tarefas"}
                                      temLink={o.tarefasVinculadas?.length > 0}
                                      isDisabled={false}
                                    />
                                  </div>
                                </div>

                                {(!isConcluido || o.responsaveis.length > 0 || o.entidadesRelacionadas?.length > 0) && (
                                  <div className="obj-linha2">
                                    <div className="obj-chips-wrap">
                                      {o.responsaveis.map((resp, respIdx) => (
                                        <ChipResponsavel
                                          key={`${o.id}-${resp.id}-${respIdx}`}
                                          responsavel={resp}
                                          onRemove={r => removerResponsavel(r, i)}
                                          disabled={isConcluido}
                                          colaboradoresAtivosIds={colaboradoresAtivosIds}
                                        />
                                      ))}
                                      {!isConcluido && (
                                        <span className="obj-add-resp-btn" title="Adicionar responsável"
                                          onClick={e => { e.stopPropagation(); setEditandoResp(prev => ({ ...prev, [i]: !prev[i] })); }}>
                                          <FontAwesomeIcon icon={faUserPlus} />
                                        </span>
                                      )}

                                      {(o.entidadesRelacionadas || []).length > 0 && <span className="obj-chips-divisor" />}
                                      {(o.entidadesRelacionadas || []).map((ent, entIdx) => (
                                        <ChipEntidade key={`${o.id}-ent-${ent.id}-${entIdx}`} entidade={ent} onRemove={e => removerEntidadeDoObjetivo(e, i)} disabled={isConcluido} />
                                      ))}
                                      {!isConcluido && (
                                        <span
                                          className="obj-add-entidade-btn"
                                          title="Vincular projeto/setor a este objetivo"
                                          ref={el => (seletorEntidadeRef.current[i] = el)}
                                          style={{ position: 'relative' }}
                                          onClick={e => { e.stopPropagation(); abrirSeletorEntidade(i); }}
                                        >
                                          <FontAwesomeIcon icon={faFolder} />

                                          {seletorEntidadeAberto[i] && (
                                            <div className="seletor-entidade-flutuante" onClick={e => e.stopPropagation()}>
                                              <div className="seletor-entidade-header">Vincular projeto/setor</div>
                                              <input
                                                type="text"
                                                autoFocus
                                                className="seletor-entidade-busca"
                                                placeholder="Buscar projeto ou setor..."
                                                value={buscaEntidadeObjetivo[i] || ""}
                                                onChange={ev => setBuscaEntidadeObjetivo(prev => ({ ...prev, [i]: ev.target.value }))}
                                              />
                                              <div className="seletor-entidade-lista">
                                                {carregandoEntidadesContainer ? (
                                                  <div className="seletor-entidade-vazio">Carregando...</div>
                                                ) : entidadesContainerFiltradas(i).length === 0 ? (
                                                  <div className="seletor-entidade-vazio">Nada encontrado</div>
                                                ) : (
                                                  entidadesContainerFiltradas(i).map(ent => {
                                                    const jaVinculada = o.entidadesRelacionadas?.some(e => e.entidadeId === ent.id && e.entidadeTipo === ent.tipo);
                                                    return (
                                                      <div
                                                        key={`${ent.tipo}-${ent.id}`}
                                                        className={`seletor-entidade-item ${jaVinculada ? 'seletor-entidade-item--ativo' : ''}`}
                                                        onClick={() => jaVinculada
                                                          ? removerEntidadeDoObjetivo(o.entidadesRelacionadas.find(e => e.entidadeId === ent.id && e.entidadeTipo === ent.tipo), i)
                                                          : adicionarEntidadeAoObjetivo(i, ent)}
                                                      >
                                                        <span className="seletor-entidade-nome">{ent.nome}</span>
                                                        <span className="seletor-entidade-tipo">{ent.tipo === 'projeto' ? 'Projeto' : 'Setor'}</span>
                                                        {jaVinculada && <span className="seletor-entidade-check">✓</span>}
                                                      </div>
                                                    );
                                                  })
                                                )}
                                              </div>
                                            </div>
                                          )}
                                        </span>
                                      )}
                                    </div>

                                    {editandoResp[i] && !isConcluido && (
                                      <div className="input-responsavel-flutuante">
                                        <input type="text" autoFocus placeholder="Nome ou @menção"
                                          value={inputResponsavel[i] || ""}
                                          onChange={e => handleResponsavelInputChange(e, i)}
                                          onBlur={() => setTimeout(() => setEditandoResp(prev => ({ ...prev, [i]: false })), 200)}
                                          onKeyDown={e => {
                                            if (e.key === "Enter") { e.preventDefault(); const v = inputResponsavel[i] || ""; if (!v.startsWith("@")) { adicionarResponsavelExterno(v, i); setEditandoResp(prev => ({ ...prev, [i]: false })); } }
                                            else if (e.key === "Escape") setEditandoResp(prev => ({ ...prev, [i]: false }));
                                          }}
                                        />
                                        {sugestoesResponsavel[i]?.length > 0 && (
                                          <div className="sugestoes-list-flutuante">
                                            {sugestoesResponsavel[i].map(item => (
                                              <div key={item.id} className="sugestao-item"
                                                onClick={() => { adicionarResponsavelInterno(item, i); setEditandoResp(prev => ({ ...prev, [i]: false })); }}>
                                                @{item.nickname || item.nome}
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    )}

                                    {!isConcluido && (
                                      <>
                                        <div className="obj-data-label" onClick={e => e.stopPropagation()}>
                                          {o.dataEntrega ? <span className="obj-data-texto">{o.dataEntrega.split('-').reverse().join('/')}</span> : null}
                                          <FontAwesomeIcon icon={faCalendar} className="obj-data-icon" />
                                          <input
                                            type="date"
                                            className="obj-date-trigger"
                                            value={o.dataEntrega || ''}
                                            onClick={e => e.stopPropagation()}
                                            onChange={async e => {
                                              e.stopPropagation();
                                              const val = e.target.value || null;
                                              const dataAnterior = o.dataEntrega || null;
                                              const novos = [...objetivosList];
                                              novos[i].dataEntrega = val;
                                              setObjetivosList(novos);
                                              if (o.id && !String(o.id).startsWith('temp')) {
                                                await supabase.from("ata_objetivos").update({ data_entrega: val }).eq("id", o.id);
                                                if (dataAnterior && dataAnterior !== val) {
                                                  const { data: registro, error } = await supabase.from("ata_objetivos_reprogramacoes")
                                                    .insert({ ata_objetivo_id: o.id, data_anterior: dataAnterior, data_nova: val, alterado_por: usuarioId })
                                                    .select().single();
                                                  if (!error && registro) {
                                                    setReprogramacoesPorObjetivo(prev => ({ ...prev, [o.id]: [registro, ...(prev[o.id] || [])] }));
                                                  }
                                                }
                                              }
                                            }}
                                          />
                                        </div>

                                        {(reprogramacoesPorObjetivo[o.id] || []).length > 0 && (
                                          <div style={{ position: 'relative' }}>
                                            <span className="obj-icon-btn" title="Histórico de reprogramação"
                                              onClick={e => { e.stopPropagation(); setHistoricoAberto(prev => ({ ...prev, [o.id]: !prev[o.id] })); }}>
                                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                                                <path d="M3 3v5h5" />
                                                <path d="M12 7v5l4 2" />
                                              </svg>
                                            </span>
                                            {historicoAberto[o.id] && (
                                              <div className="menu-historico-reprog">
                                                <div className="menu-historico-header">
                                                  <span>Histórico de reprogramação</span>
                                                  <button onClick={e => { e.stopPropagation(); setHistoricoAberto(prev => ({ ...prev, [o.id]: false })); }}>×</button>
                                                </div>
                                                {reprogramacoesPorObjetivo[o.id].map(r => (
                                                  <div key={r.id} className="menu-historico-item">
                                                    {r.data_anterior ? r.data_anterior.split('-').reverse().join('/') : '—'} → {r.data_nova ? r.data_nova.split('-').reverse().join('/') : '—'}
                                                  </div>
                                                ))}
                                              </div>
                                            )}
                                          </div>
                                        )}

                                        <span className="obj-icon-btn" title="Realocar para outra ata"
                                          onClick={e => { e.stopPropagation(); buscarAtasDisponiveis(i); }}>
                                          <FontAwesomeIcon icon={faShareFromSquare} />
                                        </span>
                                        {mostrarAtasDisponiveis[i] && (
                                          <div className="menu-atas-disponiveis">
                                            <div className="menu-atas-header">
                                              <span>Realocar para:</span>
                                              <button onClick={e => { e.stopPropagation(); setMostrarAtasDisponiveis(prev => ({ ...prev, [i]: false })); }}>×</button>
                                            </div>
                                            {atasDisponiveis.length === 0
                                              ? <div className="menu-atas-vazio">Nenhuma ata disponível</div>
                                              : atasDisponiveis.map(ata => (
                                                <div key={ata.ataId} className="menu-atas-item"
                                                  onClick={e => { e.stopPropagation(); if (window.confirm(`Realocar para "${ata.nome}"?`)) realocarObjetivo(i, ata.ataId, ata.nome); }}>
                                                  <div className="menu-atas-nome">{ata.nome}</div>
                                                  <div className="menu-atas-pauta">{ata.pauta}</div>
                                                  <div className="menu-atas-prog">
                                                    <div className="menu-atas-prog-bar"><div style={{ width: `${ata.progresso}%`, background: ata.progresso >= 75 ? '#10b981' : ata.progresso >= 50 ? '#f59e0b' : '#3b82f6', height: '100%', borderRadius: 3 }} /></div>
                                                    <span>{ata.progresso}%</span>
                                                  </div>
                                                </div>
                                              ))}
                                          </div>
                                        )}

                                        <span className="obj-icon-btn" title="Editar objetivo"
                                          onClick={e => { e.stopPropagation(); setTextoTemp(prev => ({ ...prev, [i]: o.texto })); setEditandoTexto(prev => ({ ...prev, [i]: true })); }}>
                                          <FontAwesomeIcon icon={faPen} />
                                        </span>
                                        <span className="obj-btn-excluir" title="Excluir objetivo"
                                          onClick={e => { e.stopPropagation(); removerObjetivo(i); }}>
                                          <FontAwesomeIcon icon={faTrashCan} />
                                        </span>
                                      </>
                                    )}
                                  </div>
                                )}

                                {o.tarefasVinculadas?.length > 0 && (
                                  <div className="obj-tasks-links">
                                    {o.tarefasVinculadas.map(t => (
                                      <div key={t.vinculoId} className="obj-task-link">
                                        <FontAwesomeIcon icon={faTasks} onClick={() => onOpenTask && onOpenTask(t.notaId)} />
                                        <span onClick={() => onOpenTask && onOpenTask(t.notaId)}>{t.nome}</span>
                                        <button className="obj-task-link-remover"
                                          onClick={e => { e.stopPropagation(); removerLinkNota(i, t.vinculoId); }}
                                          title="Remover vínculo">×</button>
                                      </div>
                                    ))}
                                  </div>
                                )}

                                {isConcluido && (anexosPorObjetivo[o.id] || []).length > 0 && (
                                  <div className="obj-anexos">
                                    {(anexosPorObjetivo[o.id] || []).map(anexo => (
                                      <div key={anexo.id} className="obj-anexo-item">
                                        <a href={anexo.url} target="_blank" rel="noopener noreferrer" className="obj-anexo-nome">
                                          📎 {anexo.nome_arquivo}
                                        </a>
                                        <button className="obj-anexo-remover" onClick={() => handleRemoverAnexo(anexo, o.id)}>×</button>
                                      </div>
                                    ))}
                                  </div>
                                )}

                                {showMenuPos && (
                                  <MenuPosConclusao
                                    objetivo={o}
                                    objetivoIndex={i}
                                    onNovoObjetivo={criarObjetivoInlineAposIndex}
                                    onCriarTarefa={handleCriarTarefaDoObjetivo}
                                    onClose={() => setMenuPosConclusao(null)}
                                  />
                                )}
                              </div>
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>

                {linkandoNotaIndex !== null && (
                  <div className="modal-linkar-nota-overlay" onClick={fecharModalLink}>
                    <div className="modal-linkar-nota-box" onClick={e => e.stopPropagation()}>
                      <div className="modal-linkar-nota-header">
                        <span>
                          {notaLinkView === 'notas' && (
                            <button className="modal-linkar-nota-voltar"
                              onClick={() => { setNotaLinkView('entidades'); setEntidadeSelecionada(null); setBuscaLinkNota(""); }}
                              title="Voltar">←</button>
                          )}
                          {notaLinkView === 'entidades' ? 'Selecione o projeto/setor' : `Tarefas em: ${entidadeSelecionada?.nome}`}
                        </span>
                        <button onClick={fecharModalLink}>×</button>
                      </div>

                      {!carregandoNotas && (entidadesDisponiveis.length > 0 || notasDisponiveis.length > 0) && (
                        <div className="modal-linkar-nota-busca">
                          <input type="text" autoFocus
                            placeholder={notaLinkView === 'entidades' ? "Buscar projeto ou setor..." : "Buscar tarefa..."}
                            value={buscaLinkNota} onChange={e => setBuscaLinkNota(e.target.value)} />
                          {buscaLinkNota && <button className="modal-linkar-nota-busca-limpar" onClick={() => setBuscaLinkNota("")}>×</button>}
                        </div>
                      )}

                      {carregandoNotas ? (
                        <div className="modal-linkar-nota-loading">Carregando...</div>
                      ) : notaLinkView === 'entidades' ? (
                        entidadesFiltradas.length === 0 ? (
                          <div className="modal-linkar-nota-vazio">Nenhum projeto ou setor encontrado</div>
                        ) : (
                          <div className="modal-linkar-nota-lista">
                            {entidadesFiltradas.map(ent => (
                              <button key={`${ent.tipo}-${ent.id}`} className="modal-linkar-nota-item modal-linkar-nota-item--entidade"
                                onClick={() => buscarNotasDaEntidade(ent)}>
                                <div className="modal-linkar-nota-avatar">
                                  {ent.foto ? <img src={ent.foto} alt={ent.nome} onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} /> : null}
                                  <div className="modal-linkar-nota-avatar-fallback" style={{ display: ent.foto ? 'none' : 'flex' }}>{ent.nome.charAt(0).toUpperCase()}</div>
                                </div>
                                <div className="modal-linkar-nota-entidade-info">
                                  <div className="modal-linkar-nota-nome">{ent.nome}</div>
                                  <div className="modal-linkar-nota-tipo">{ent.tipo === 'projeto' ? 'Projeto' : 'Setor'}</div>
                                </div>
                              </button>
                            ))}
                          </div>
                        )
                      ) : notasFiltradasBusca.length === 0 ? (
                        <div className="modal-linkar-nota-vazio">Nenhuma tarefa encontrada</div>
                      ) : (
                        <div className="modal-linkar-nota-lista">
                          {notasFiltradasBusca.map(nota => {
                            const jaVinculada = objetivosList[linkandoNotaIndex]?.tarefasVinculadas?.some(t => t.notaId === nota.id);
                            return (
                              <button key={nota.id}
                                className={`modal-linkar-nota-item ${jaVinculada ? 'modal-linkar-nota-item--vinculada' : ''}`}
                                onClick={() => !jaVinculada && linkarNotaAoObjetivo(nota, linkandoNotaIndex)}
                                disabled={jaVinculada}>
                                <div className="modal-linkar-nota-nome">{nota.nome}</div>
                                <div className="modal-linkar-nota-classificacao">{nota.pilhaNome || 'Sem pilha'}</div>
                                <div className="modal-linkar-nota-tipo">{jaVinculada ? "✓ Já vinculada" : nota.tipo}</div>
                              </button>
                            );
                          })}
                        </div>
                      )}

                      {objetivosList[linkandoNotaIndex]?.tarefasVinculadas?.length > 0 && (
                        <div className="modal-linkar-nota-footer">
                          <div className="modal-linkar-nota-footer-titulo">Vinculadas:</div>
                          {objetivosList[linkandoNotaIndex].tarefasVinculadas.map(t => (
                            <div key={t.vinculoId} className="modal-linkar-nota-vinculo-item">
                              <span>{t.nome}</span>
                              <button onClick={() => removerLinkNota(linkandoNotaIndex, t.vinculoId)}>×</button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </DragDropContext>
            </div>

            {(todosResponsaveis.length > 0 || todasEntidades.length > 0 || objetivosList.length > 0) && (
              <div className="objetivos-footer-filtro">
                <div className="objetivos-footer-filtro-linha">
                  <div className="objetivos-footer-filtro-esquerda">
                    <svg className={`icone-filtro-objetivos ${filtroResponsavel ? 'icone-filtro-ativo' : ''}`}
                      width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                      strokeLinecap="round" strokeLinejoin="round" title="Filtrar por responsável">
                      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                    </svg>
                    {todosResponsaveis.map(resp => {
                      const key = resp.usuario_id || resp.nome_externo;
                      const isAtivo = filtroResponsavel === key;
                      const nomeExibicao = resp.nome_exibicao || resp.nome_externo || "Usuário";
                      return (
                        <div key={key}
                          className={`filtro-avatar-wrapper ${isAtivo ? 'filtro-avatar-ativo' : ''}`}
                          title={`Filtrar por ${nomeExibicao}`}
                          onClick={() => setFiltroResponsavel(prev => prev === key ? null : key)}>
                          {resp.avatar_url ? <img src={resp.avatar_url} alt={nomeExibicao} className="filtro-avatar-img" onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} /> : null}
                          <div className="filtro-avatar-iniciais" style={{ display: resp.avatar_url ? 'none' : 'flex' }}>{gerarAbreviacao(nomeExibicao)}</div>
                          {isAtivo && <div className="filtro-avatar-badge" />}
                        </div>
                      );
                    })}
                    {filtroResponsavel && <button className="filtro-limpar-btn" onClick={() => setFiltroResponsavel(null)}>✕</button>}
                  </div>

                  <div className="filtro-status-wrap" style={{ display: 'flex', flexDirection: 'column', gap: 1, fontSize: 10, color: '#6b7280' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', lineHeight: 1.2, margin: 0, fontSize: 10.5 }}>
                      <input type="checkbox" style={{ width: 11, height: 11, margin: 0 }} checked={arquivarConcluidos} onChange={e => setArquivarConcluidos(e.target.checked)} />
                      Arquivar concluídos
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', lineHeight: 1.2, margin: 0, fontSize: 10.5 }}>
                      <input type="checkbox" style={{ width: 11, height: 11, margin: 0 }} checked={soSemResp} onChange={e => setSoSemResp(e.target.checked)} />
                      Sem responsáveis
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', lineHeight: 1.2, margin: 0, fontSize: 10.5 }}>
                      <input type="checkbox" style={{ width: 11, height: 11, margin: 0 }} checked={soEmAberto} onChange={e => setSoEmAberto(e.target.checked)} />
                      Em aberto
                    </label>
                  </div>
                </div>

                {todasEntidades.length > 0 && (
                  <div className="objetivos-footer-filtro-linha objetivos-footer-filtro-entidades">
                    <FontAwesomeIcon icon={faFolder} className={`icone-filtro-objetivos ${filtroEntidade ? 'icone-filtro-ativo' : ''}`} title="Filtrar por projeto/setor" />
                    {todasEntidades.map(ent => {
                      const key = `${ent.entidadeTipo}-${ent.entidadeId}`;
                      const isAtivo = filtroEntidade === key;
                      return (
                        <div key={key}
                          className={`filtro-entidade-badge ${ent.entidadeTipo === 'projeto' ? 'filtro-entidade-badge--projeto' : 'filtro-entidade-badge--setor'} ${isAtivo ? 'filtro-entidade-badge-ativo' : ''}`}
                          title={`Filtrar por ${ent.entidadeNome}`}
                          onClick={() => setFiltroEntidade(prev => prev === key ? null : key)}>
                          {ent.entidadeNome}
                        </div>
                      );
                    })}
                    {filtroEntidade && <button className="filtro-limpar-btn" onClick={() => setFiltroEntidade(null)}>✕</button>}
                  </div>
                )}
              </div>
            )}

          </div>
        </div>
      )}

      {criandoTarefaIndex !== null && (
        <div className="modal-linkar-nota-overlay" onClick={fecharModalCriarTarefa}>
          <div className="modal-linkar-nota-box" onClick={e => e.stopPropagation()}>
            <div className="modal-linkar-nota-header">
              <span>
                {tarefaView === 'pilhas' && (
                  <button className="modal-linkar-nota-voltar"
                    onClick={() => { setTarefaView('entidades'); setEntidadeSelecionadaTarefa(null); setBuscaTarefa(""); }}
                    title="Voltar">←</button>
                )}
                {tarefaView === 'entidades' ? 'Selecione o projeto/setor' : `Pilhas em: ${entidadeSelecionadaTarefa?.nome}`}
              </span>
              <button onClick={fecharModalCriarTarefa}>×</button>
            </div>

            {!carregandoPilhasTarefa && (entidadesDisponivelsTarefa.length > 0 || pilhasDisponivelsTarefa.length > 0) && (
              <div className="modal-linkar-nota-busca">
                <input type="text" autoFocus
                  placeholder={tarefaView === 'entidades' ? "Buscar projeto ou setor..." : "Buscar pilha..."}
                  value={buscaTarefa} onChange={e => setBuscaTarefa(e.target.value)} />
                {buscaTarefa && <button className="modal-linkar-nota-busca-limpar" onClick={() => setBuscaTarefa("")}>×</button>}
              </div>
            )}

            {carregandoPilhasTarefa ? (
              <div className="modal-linkar-nota-loading">Carregando...</div>
            ) : tarefaView === 'entidades' ? (
              entidadesFiltradas2.length === 0 ? (
                <div className="modal-linkar-nota-vazio">Nenhum projeto ou setor encontrado</div>
              ) : (
                <div className="modal-linkar-nota-lista">
                  {entidadesFiltradas2.map(ent => (
                    <button key={`${ent.tipo}-${ent.id}`} className="modal-linkar-nota-item modal-linkar-nota-item--entidade"
                      onClick={() => buscarPilhasDaEntidade(ent)}>
                      <div className="modal-linkar-nota-avatar">
                        {ent.foto ? <img src={ent.foto} alt={ent.nome} onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} /> : null}
                        <div className="modal-linkar-nota-avatar-fallback" style={{ display: ent.foto ? 'none' : 'flex' }}>{ent.nome.charAt(0).toUpperCase()}</div>
                      </div>
                      <div className="modal-linkar-nota-entidade-info">
                        <div className="modal-linkar-nota-nome">{ent.nome}</div>
                        <div className="modal-linkar-nota-tipo">{ent.tipo === 'projeto' ? 'Projeto' : 'Setor'}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )
            ) : pilhasFiltradasBusca.length === 0 ? (
              <div className="modal-linkar-nota-vazio">Nenhuma pilha encontrada</div>
            ) : (
              <div className="modal-linkar-nota-lista">
                {pilhasFiltradasBusca.map(pilha => (
                  <button key={pilha.id} className="modal-linkar-nota-item" onClick={() => confirmarCriacaoTarefa(pilha.id)}>
                    <div className="modal-linkar-nota-nome">{pilha.nome}</div>
                    <div className="modal-linkar-nota-tipo">{pilha.tipo_pilha || 'Pilha'}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}